import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import createEnhancedFlooring from './Flooring';
import { createCannabisLeaf } from './plant/Leaf';
import { Heart, Star, Award, TrendingUp } from 'lucide-react';
import { 
  getPlantCharacteristics,
  updatePlantWithStrainLogic,
  getToolEffectiveness,
  getStrainTooltipInfo,
  applyStrainSpecificDegradation 
} from './GameState'
import { useUser } from '../context/UserContext';
import GrowRoomAPI from '../services/growRoomApi';
// Plant types configuration
import { plantTypes, getRarityColor, getRarityGlow } from '../data/data';
import AuthGate from '../auth/AuthGate';
const CannabisShop = React.lazy(() => import('./Shop'));

const CannabisGrowRoom = () => {

   const { 
     user, 
     isAuthenticated, 
     logout, 
     userStats, 
     statsLoading 
   } = useUser();
  
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const plantsRef = useRef([]);
  const potsRef = useRef([]);
  const raycasterRef = useRef(null);

    const [showShop, setShowShop] = useState(false);
  const [shopCatalog, setShopCatalog] = useState(null);
  const [shopLoading, setShopLoading] = useState(false);
  const [purchaseHistory, setPurchaseHistory] = useState([]);


   

  const mouseRef = useRef(new THREE.Vector2());
  const animationIdRef = useRef(null);
  const isInitializedRef = useRef(false);

  const isDraggingRef = useRef(false);
  const previousMouseYRef = useRef(0);
  const verticalAngleRef = useRef(0); 

  const [hasGrowRoom, setHasGrowRoom] = useState(null); // null = loading
  const [showCreateGrowRoom, setShowCreateGrowRoom] = useState(false);
  const [loading, setLoading] = useState(true);


  const handleMouseDown = (event) => {
    if (event.button === 0) { // Left click
      const rect = mountRef.current.getBoundingClientRect();
      mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);
      const intersects = raycasterRef.current.intersectObjects([...potsRef.current.filter(p => p)], true);

      console.log(`Mouse down - intersects: ${intersects.length}`);

      if (intersects.length > 0) {
        const object = intersects[0].object;
        console.log(`Clicked object type: ${object.userData.type}`);
        
        if (object.userData.type === 'pot') {
          const potIndex = object.userData.id;
          
          // FIXED: Check if pot has plant by looking at the visual, not gameState
          const hasPlant = plantsRef.current[potIndex] !== null && plantsRef.current[potIndex] !== undefined;
          
          console.log(`Pot index: ${potIndex}, has plant: ${hasPlant}`);
          
          if (hasPlant) {
            addNotification('🌱 Cannot move pot with plant! Harvest or remove plant first.');
            return;
          }

          console.log(`Starting drag for pot ${potIndex}`);

          const level = userStats?.level || 1;
          const unlockedPotCount = level >= 30 ? 19 : level >= 20 ? 15 : level >= 10 ? 10 : 5;
          console.log(`Level: ${level}, Available Pots: ${unlockedPotCount}`);
          console.log(`Total Grid Positions: ${allGridPositions.length}`);

          // SIMPLIFIED: Just show all free positions
          const occupiedPositions = [];
          const freePositions = [];

          // Check all 19 grid positions
          for (let gridIndex = 0; gridIndex < allGridPositions.length; gridIndex++) {
            const isOccupied = isPositionOccupied(gridIndex, potIndex); // Exclude current pot
            if (isOccupied) {
              occupiedPositions.push(gridIndex);
            } else {
              freePositions.push(gridIndex);
            }
          }

          console.log(`Occupied grid positions: ${occupiedPositions}`);
          console.log(`FREE grid positions: ${freePositions}`);
          console.log(`You can move pot ${potIndex} to any of these ${freePositions.length} FREE positions!`);

          setIsDraggingPot(true);
          setDraggedPotIndex(potIndex);
          object.material.emissive.setHex(0x444444);
          addNotification(`🏺 Dragging Pot #${potIndex + 1}. Drop on any free grid position.`);
          
          return;
        }
      }

      // Only start camera drag if we didn't click a pot
      isDraggingRef.current = true;
      previousMouseYRef.current = event.clientY;
    }
  };

  const handleMouseUp = () => {
    console.log(`Mouse up - isDraggingPot: ${isDraggingPot}, draggedPotIndex: ${draggedPotIndex}`);
    
    if (isDraggingPot && draggedPotIndex !== null) {
      const potMesh = potsRef.current[draggedPotIndex];
      if (potMesh) {
        console.log(`Finalizing pot drop at: ${potMesh.position.x}, ${potMesh.position.z}`);
        
        // Get the grid index for current position
        const snapResult = snapToGrid(potMesh.position.x, potMesh.position.z);
        const targetIndex = snapResult.gridIndex;
        
        console.log(`Final target grid index: ${targetIndex}`);
        
        if (targetIndex >= 0 && targetIndex < allGridPositions.length) {
          const isOccupied = isPositionOccupied(targetIndex, draggedPotIndex);
          
          if (!isOccupied) {
            // Position is free - try to save to backend
            swapPotPositions(draggedPotIndex, targetIndex);
            console.log(`Successfully moved pot ${draggedPotIndex} to grid ${targetIndex}`);
          } else {
            console.log(`Position ${targetIndex} is occupied - reverting`);
            // Revert to original position
            revertPotToOriginalPosition(draggedPotIndex);
            addNotification('❌ Position occupied! Pot reverted to original spot.');
          }
        } else {
          console.log(`Invalid target index: ${targetIndex}`);
          revertPotToOriginalPosition(draggedPotIndex);
          addNotification('❌ Invalid drop position!');
        }
        
        // Reset visual state
        potMesh.material.color.setHex(0x4A4A4A);
        potMesh.material.emissive.setHex(0x000000);
      }
    }

    // Reset all drag states
    isDraggingRef.current = false;
    setIsDraggingPot(false);
    setDraggedPotIndex(null);
    console.log('All drag states reset');
  };

  // ADD HELPER FUNCTION TO REVERT POT POSITION:
  const revertPotToOriginalPosition = (potIndex) => {
    // Get original position from backend data or fallback to default
    let originalPos;
    
    if (gameState.pots && gameState.pots[potIndex]) {
      originalPos = [gameState.pots[potIndex].position_x, gameState.pots[potIndex].position_z];
    } else {
      // Fallback to default grid position
      originalPos = allGridPositions[potIndex] || allGridPositions[0];
    }
    
    const potMesh = potsRef.current[potIndex];
    if (potMesh) {
      potMesh.position.x = originalPos[0];
      potMesh.position.z = originalPos[1];
    }
    
    const soilMesh = soilRef.current[potIndex];
    if (soilMesh) {
      soilMesh.position.x = originalPos[0];
      soilMesh.position.z = originalPos[1];
    }
    
    const plantMesh = plantsRef.current[potIndex];
    if (plantMesh) {
      plantMesh.position.x = originalPos[0];
      plantMesh.position.z = originalPos[1];
    }
    
    console.log(`Reverted pot ${potIndex} to original position:`, originalPos);
  };

  const swapPotPositions = async (draggedPotIndex, targetGridIndex) => {
    console.log(`Moving pot ${draggedPotIndex} to grid position ${targetGridIndex}`);
    
    if (targetGridIndex < 0 || targetGridIndex >= allGridPositions.length) {
      console.log(`Invalid target grid index: ${targetGridIndex}`);
      addNotification('❌ Invalid position!');
      return;
    }
    
    const targetPos = allGridPositions[targetGridIndex];
    
    try {
      // 🔧 NEW: Call backend to save position
      const response = await GrowRoomAPI.updatePotPosition(draggedPotIndex, targetPos[0], targetPos[1]);
      
      if (!response.success) {
        addNotification(`❌ ${response.error}`);
        return;
      }
      
      // Update visual position after successful backend update
      const potMesh = potsRef.current[draggedPotIndex];
      if (potMesh) {
        potMesh.position.x = targetPos[0];
        potMesh.position.z = targetPos[1];
        console.log(`Pot moved to: ${targetPos[0]}, ${targetPos[1]}`);
      }

      const soilMesh = soilRef.current[draggedPotIndex];
      if (soilMesh) {
        soilMesh.position.x = targetPos[0];
        soilMesh.position.z = targetPos[1];
        console.log(`Soil moved with pot to: ${targetPos[0]}, ${targetPos[1]}`);
      }
      
      // If there's a plant in this pot, move it too
      const plantMesh = plantsRef.current[draggedPotIndex];
      if (plantMesh) {
        plantMesh.position.x = targetPos[0];
        plantMesh.position.z = targetPos[1];
        console.log(`Plant moved with pot to: ${targetPos[0]}, ${targetPos[1]}`);
      }
      
      addNotification(`🏺 Pot ${draggedPotIndex + 1} moved to position (${targetPos[0]}, ${targetPos[1]})!`);
      
    } catch (error) {
      console.error('Error updating pot position:', error);
      addNotification('❌ Failed to save pot position');
    }
  };

  // Also update your handleMouseMoveDrag function to better handle the raycasting:
  const handleMouseMoveDrag = (event) => {
    // Existing camera dragging code
    if (isDraggingRef.current && cameraRef.current) {
      const deltaY = event.clientY - previousMouseYRef.current;
      previousMouseYRef.current = event.clientY;

      verticalAngleRef.current = Math.max(
        -Math.PI / 4,
        Math.min(Math.PI / 4, verticalAngleRef.current - deltaY * 0.005)
      );

      const radius = 12;
      const x = Math.sin(0) * radius;
      const z = Math.cos(0) * radius;
      const y = 8 + Math.tan(verticalAngleRef.current) * 6;

      cameraRef.current.position.set(x, y, z);
      cameraRef.current.lookAt(0, 2, 0);
    }

    // FIXED pot dragging logic
    if (isDraggingPot && draggedPotIndex !== null && mountRef.current && cameraRef.current && raycasterRef.current) {
      console.log('POT DRAG MOVE EVENT!');
      
      const rect = mountRef.current.getBoundingClientRect();
      const mouseX = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const mouseY = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      
      console.log(`Mouse normalized coords: ${mouseX}, ${mouseY}`);
      
      // Set up raycaster
      const mouse = new THREE.Vector2(mouseX, mouseY);
      raycasterRef.current.setFromCamera(mouse, cameraRef.current);
      
      // Create a ground plane for intersection
      const groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -0.8); // Ground level at y=0.8 (pot height)
      const intersectionPoint = new THREE.Vector3();
      
      // Find intersection with ground
      if (raycasterRef.current.ray.intersectPlane(groundPlane, intersectionPoint)) {
        console.log(`World intersection: ${intersectionPoint.x}, ${intersectionPoint.z}`);
        
        // Snap to grid
        const snapped = snapToGrid(intersectionPoint.x, intersectionPoint.z);
        console.log(`Snapped to: ${snapped.x}, ${snapped.z}`);
        
        const potMesh = potsRef.current[draggedPotIndex];
        if (potMesh) {
          potMesh.position.x = snapped.x;
          potMesh.position.z = snapped.z;
          potMesh.material.color.setHex(0x44ff44); // Green while dragging
          console.log(`Pot mesh position updated to: ${potMesh.position.x}, ${potMesh.position.z}`);
        }
        const soilMesh = soilRef.current[draggedPotIndex];
        if (soilMesh) {
          soilMesh.position.x = snapped.x;
          soilMesh.position.z = snapped.z;
          console.log(`Soil position updated to: ${soilMesh.position.x}, ${soilMesh.position.z}`);
        }

      } else {
        console.log('No intersection with ground plane');
      }
    }
  };






  // useEffect(() => {
  //   if (isAuthenticated && user) {
  //     checkGrowRoomStatus();
  //   }
  // }, [isAuthenticated, user]);

  const checkGrowRoomStatus = async () => {
    try {
      setLoading(true);
      const response = await GrowRoomAPI.getGrowRoomStatus();
      
      if (response.success) {
        if (response.has_grow_room) {
          setHasGrowRoom(true);
          await loadGrowRoomData();
        } else {
          setHasGrowRoom(false);
          setShowCreateGrowRoom(true);
        }
      } else {
        addNotification('❌ Error checking grow room status');
      }
    } catch (error) {
      console.error('Error checking grow room status:', error);
      addNotification('❌ Connection error');
    } finally {
      setLoading(false);
    }
  };




  const [showInventory, setShowInventory] = useState(false);
  
  const [gameState, setGameState] = useState({
    points: 0,  
    streak: 0,
    gardenHealth: 0,
    lastVisit: new Date().toDateString(),
    activeTool: null,
    plants: [],
    totalHarvests: 0, 
    // Player's seed inventory
    inventory: {
      'Blue Dream': 3,
      'Northern Lights': 2,
      'Green Crack': 2,
      'Jack Herer': 1,
      'White Widow': 1,
      'Purple Haze': 0,
      'Sour Diesel': 0,
      'Girl Scout Cookies': 0,
      'OG Kush': 0,
      'Gorilla Glue #4': 0,
      
      'Golden Goat': 0,
      
      'Blue Moonshine': 0,
      
      'Pineapple Express': 0

    }
  });

  const [notifications, setNotifications] = useState([]);
  const [tooltip, setTooltip] = useState(null);
  const [showDailyBonus, setShowDailyBonus] = useState(false);
  const [bonusPoints, setBonusPoints] = useState(0);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(null);
  const [showPlantInventory, setShowPlantInventory] = useState(null); // New state for plant selection
  const [selectedPotId, setSelectedPotId] = useState(null); // Track which pot to plant in

  const [isDraggingPot, setIsDraggingPot] = useState(false);
  const [draggedPotIndex, setDraggedPotIndex] = useState(null);


  const allPositions = [
    [-6, -4], [-3, -4], [0, -4], [3, -4], [6, -4],
    [-6, -1], [-3, -1], [0, -1], [3, -1], [6, -1],
    [-6, 2], [-3, 2], [0, 2], [3, 2], [6, 2],
    [-4, 5], [-1, 5], [2, 5], [5, 5]
  ];

  const allGridPositions = [
    [-6, -4], [-3, -4], [0, -4], [3, -4], [6, -4],
    [-6, -1], [-3, -1], [0, -1], [3, -1], [6, -1],
    [-6, 2], [-3, 2], [0, 2], [3, 2], [6, 2],
    [-4, 5], [-1, 5], [2, 5], [5, 5]
  ];


  // Initialize Three.js scene
  const initScene = useCallback(() => {
  if (!mountRef.current || isInitializedRef.current || rendererRef.current) {
    return isInitializedRef.current; // Return true if already initialized
  }

    console.log('Initializing scene...');
    
    try {
      while (mountRef.current.firstChild) {
        mountRef.current.removeChild(mountRef.current.firstChild);
      }

      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0x87CEEB);
      sceneRef.current = scene;

      const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
      camera.position.set(0, 8, 12);
      camera.lookAt(0, 0, 0);
      cameraRef.current = camera;

      const renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      rendererRef.current = renderer;

      mountRef.current.appendChild(renderer.domElement);

      const ambientLight = new THREE.AmbientLight(0x404040, 0.6);
      scene.add(ambientLight);

      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
      directionalLight.position.set(10, 10, 5);
      directionalLight.castShadow = true;
      directionalLight.shadow.mapSize.width = 2048;
      directionalLight.shadow.mapSize.height = 2048;
      scene.add(directionalLight);

      const growLight = new THREE.PointLight(0xff00ff, 0.3, 20);
      growLight.position.set(0, 6, 0);
      scene.add(growLight);

      createEnhancedFlooring(scene);
      createWalls(scene);
      raycasterRef.current = new THREE.Raycaster();
      isInitializedRef.current = true;

      console.log('Scene initialized successfully');
      return true; // ADD THIS

    } catch (error) {
      console.error('Scene init failed:', error);
      return false; // ADD THIS
    }
  }, []);



const createWalls = (scene) => {
  // Create glass material - more transparent with warmer tint
  const glassMaterial = new THREE.MeshLambertMaterial({ 
    color: 0xaaffcc,
    transparent: true, 
    opacity: 0.15,
    side: THREE.DoubleSide
  });
  
  // Create white/aluminum frame material
  const frameMaterial = new THREE.MeshLambertMaterial({ 
    color: 0xcccccc 
  });
  
  // Size the greenhouse to match the garden area better
  const greenhouseWidth = 16;
  const greenhouseDepth = 12;
  const wallHeight = 8;
  
  // Back wall
  createGreenhouseWall(scene, 0, wallHeight/2, -greenhouseDepth/2, 0, glassMaterial, frameMaterial, greenhouseWidth, wallHeight);
  
  // Left side wall
  createGreenhouseWall(scene, -greenhouseWidth/2, wallHeight/2, 0, Math.PI / 2, glassMaterial, frameMaterial, greenhouseDepth, wallHeight);
  
  // Right side wall  
  createGreenhouseWall(scene, greenhouseWidth/2, wallHeight/2, 0, -Math.PI / 2, glassMaterial, frameMaterial, greenhouseDepth, wallHeight);
  
  // Just add the sun - no roof
  createSun(scene);
};

const createGreenhouseWall = (scene, x, y, z, rotationY, glassMaterial, frameMaterial, width, height) => {
  const wallGroup = new THREE.Group();
  
  // Main glass panel
  const glassGeometry = new THREE.PlaneGeometry(width, height);
  const glassPanel = new THREE.Mesh(glassGeometry, glassMaterial);
  wallGroup.add(glassPanel);
  
  // Vertical frame posts (every 4 units)
  const postGeometry = new THREE.BoxGeometry(0.2, height, 0.2);
  for (let i = -width/2; i <= width/2; i += 4) {
    const post = new THREE.Mesh(postGeometry, frameMaterial);
    post.position.set(i, 0, 0.1);
    wallGroup.add(post);
  }
  
  // Horizontal frame beams (every 2.5 units)
  const beamGeometry = new THREE.BoxGeometry(width, 0.2, 0.2);
  for (let i = -height/2; i <= height/2; i += 2.5) {
    const beam = new THREE.Mesh(beamGeometry, frameMaterial);
    beam.position.set(0, i, 0.1);
    wallGroup.add(beam);
  }
  
  // Corner reinforcements
  const cornerGeometry = new THREE.BoxGeometry(0.3, 0.3, 0.3);
  const corners = [
    [-width/2, -height/2, 0.1],
    [width/2, -height/2, 0.1],
    [-width/2, height/2, 0.1],
    [width/2, height/2, 0.1]
  ];
  
  corners.forEach(([cx, cy, cz]) => {
    const corner = new THREE.Mesh(cornerGeometry, frameMaterial);
    corner.position.set(cx, cy, cz);
    wallGroup.add(corner);
  });
  
  // Position and rotate the wall group
  wallGroup.position.set(x, y, z);
  wallGroup.rotation.y = rotationY;
  
  scene.add(wallGroup);
};



const createSun = (scene) => {
  // Create the main sun body - visible in the scene
  const sunGeometry = new THREE.SphereGeometry(1.5, 32, 32);
  const sunMaterial = new THREE.MeshBasicMaterial({ 
    color: 0xffff00,
    transparent: false,
    emissive: 0xffaa00,
    emissiveIntensity: 0.8
  });
  const sun = new THREE.Mesh(sunGeometry, sunMaterial);
  
  // Position sun where it's actually visible from camera (camera is at 0, 8, 12)
  sun.position.set(8, 12, -5); // Much closer and visible
  scene.add(sun);
  
  // Create DYNAMIC and ANGLED sun rays that look realistic
  const createDynamicSunRays = () => {
    const rayGroup = new THREE.Group();
    const sunPos = new THREE.Vector3(8, 12, -5);
    const gardenCenter = new THREE.Vector3(0, 0, 0);
    
    // Main directional rays - these angle FROM the sun TO various points in the garden
    const mainRayCount = 12;
    for (let i = 0; i < mainRayCount; i++) {
      // Create target points around and in the garden
      const angle = (i / mainRayCount) * Math.PI * 2;
      const targetRadius = 3 + Math.random() * 4;
      const targetPoint = new THREE.Vector3(
        Math.cos(angle) * targetRadius,
        -1 + Math.random() * 2, // Vary the target height
        Math.sin(angle) * targetRadius
      );
      
      // Calculate direction from sun to target
      const direction = new THREE.Vector3().subVectors(targetPoint, sunPos).normalize();
      const distance = sunPos.distanceTo(targetPoint);
      
      // Create ray geometry that follows this direction
      const rayGeometry = new THREE.PlaneGeometry(0.4 + Math.random() * 0.3, distance * 0.8);
      const rayMaterial = new THREE.MeshBasicMaterial({
        color: new THREE.Color().setHSL(0.15 + Math.random() * 0.1, 0.3, 0.8), // Warm yellow variations
        transparent: true,
        opacity: 0.15 + Math.random() * 0.15,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending
      });
      
      const ray = new THREE.Mesh(rayGeometry, rayMaterial);
      
      // Position ray between sun and target
      const midPoint = new THREE.Vector3().addVectors(sunPos, targetPoint).multiplyScalar(0.5);
      ray.position.copy(midPoint);
      
      // Orient ray to point from sun to target
      ray.lookAt(targetPoint);
      ray.rotateX(Math.PI / 2); // Adjust for plane geometry orientation
      
      // Add some natural variation
      ray.rotation.z += (Math.random() - 0.5) * 0.2;
      
      // Store original properties for animation
      ray.userData = {
        originalOpacity: ray.material.opacity,
        animationPhase: Math.random() * Math.PI * 2,
        pulseSpeed: 0.5 + Math.random() * 1.0,
        direction: direction.clone()
      };
      
      rayGroup.add(ray);
    }
    
    // Add scattered atmospheric rays for volume
    const atmosphereRayCount = 8;
    for (let i = 0; i < atmosphereRayCount; i++) {
      // Create rays that scatter in different directions
      const scatterAngle = Math.random() * Math.PI * 2;
      const scatterElevation = Math.random() * Math.PI * 0.3; // Don't go too low
      
      const scatterDirection = new THREE.Vector3(
        Math.cos(scatterAngle) * Math.cos(scatterElevation),
        -Math.sin(scatterElevation),
        Math.sin(scatterAngle) * Math.cos(scatterElevation)
      );
      
      const rayLength = 6 + Math.random() * 4;
      const rayGeometry = new THREE.PlaneGeometry(0.8 + Math.random() * 0.4, rayLength);
      const rayMaterial = new THREE.MeshBasicMaterial({
        color: new THREE.Color().setHSL(0.16, 0.2, 0.9),
        transparent: true,
        opacity: 0.08 + Math.random() * 0.06,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending
      });
      
      const ray = new THREE.Mesh(rayGeometry, rayMaterial);
      
      // Position ray starting from sun area
      const startPos = sunPos.clone().add(
        new THREE.Vector3(
          (Math.random() - 0.5) * 2,
          (Math.random() - 0.5) * 2,
          (Math.random() - 0.5) * 2
        )
      );
      
      ray.position.copy(startPos).add(scatterDirection.clone().multiplyScalar(rayLength * 0.5));
      
      // Orient ray along scatter direction
      ray.lookAt(startPos.clone().add(scatterDirection.clone().multiplyScalar(rayLength)));
      ray.rotateX(Math.PI / 2);
      
      ray.userData = {
        originalOpacity: ray.material.opacity,
        animationPhase: Math.random() * Math.PI * 2,
        pulseSpeed: 0.3 + Math.random() * 0.5,
        isAtmosphere: true
      };
      
      rayGroup.add(ray);
    }
    
    // Add god rays - these are the dramatic angled beams
    const godRayCount = 6;
    for (let i = 0; i < godRayCount; i++) {
      // Create dramatic angled beams
      const godRayAngle = (i / godRayCount) * Math.PI * 2 + Math.random() * 0.3;
      const godRayLength = 8 + Math.random() * 3;
      
      // Angle these more dramatically
      const godDirection = new THREE.Vector3(
        Math.cos(godRayAngle) * 0.8,
        -0.6 - Math.random() * 0.3, // More angled downward
        Math.sin(godRayAngle) * 0.8
      ).normalize();
      
      const godGeometry = new THREE.PlaneGeometry(0.6, godRayLength);
      const godMaterial = new THREE.MeshBasicMaterial({
        color: 0xffffaa,
        transparent: true,
        opacity: 0.12,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending
      });
      
      const godRay = new THREE.Mesh(godGeometry, godMaterial);
      
      // Position god ray
      const godStart = sunPos.clone().add(new THREE.Vector3(
        (Math.random() - 0.5) * 1,
        -1,
        (Math.random() - 0.5) * 1
      ));
      
      godRay.position.copy(godStart).add(godDirection.clone().multiplyScalar(godRayLength * 0.5));
      godRay.lookAt(godStart.clone().add(godDirection.clone().multiplyScalar(godRayLength)));
      godRay.rotateX(Math.PI / 2);
      
      godRay.userData = {
        originalOpacity: godMaterial.opacity,
        animationPhase: Math.random() * Math.PI * 2,
        pulseSpeed: 0.2 + Math.random() * 0.3,
        isGodRay: true
      };
      
      rayGroup.add(godRay);
    }
    
    // Add dust motes/particles in the rays
    const createDustMotes = () => {
      const dustGroup = new THREE.Group();
      const dustCount = 30;
      
      for (let i = 0; i < dustCount; i++) {
        const dustGeometry = new THREE.SphereGeometry(0.01 + Math.random() * 0.01, 4, 4);
        const dustMaterial = new THREE.MeshBasicMaterial({
          color: 0xffffdd,
          transparent: true,
          opacity: 0.3 + Math.random() * 0.4,
          blending: THREE.AdditiveBlending
        });
        
        const dustMote = new THREE.Mesh(dustGeometry, dustMaterial);
        
        // Position dust motes in the sunray areas
        dustMote.position.set(
          -4 + Math.random() * 8,
          2 + Math.random() * 6,
          -4 + Math.random() * 8
        );
        
        dustMote.userData = {
          velocity: new THREE.Vector3(
            (Math.random() - 0.5) * 0.02,
            -0.01 - Math.random() * 0.01,
            (Math.random() - 0.5) * 0.02
          ),
          originalOpacity: dustMaterial.opacity,
          bobPhase: Math.random() * Math.PI * 2
        };
        
        dustGroup.add(dustMote);
      }
      
      return dustGroup;
    };
    
    const dustMotes = createDustMotes();
    rayGroup.add(dustMotes);
    
    return rayGroup;
  };
  
  // Add the enhanced sun rays
  const sunRays = createDynamicSunRays();
  scene.add(sunRays);
  
  // Create enhanced sun glow with multiple layers
  const createSunGlow = () => {
    const glowGroup = new THREE.Group();
    
    // Inner bright glow
    const innerGlowGeometry = new THREE.SphereGeometry(2.2, 16, 16);
    const innerGlowMaterial = new THREE.MeshBasicMaterial({
      color: 0xffff88,
      transparent: true,
      opacity: 0.25,
      blending: THREE.AdditiveBlending
    });
    const innerGlow = new THREE.Mesh(innerGlowGeometry, innerGlowMaterial);
    glowGroup.add(innerGlow);
    
    // Outer soft glow
    const outerGlowGeometry = new THREE.SphereGeometry(3.5, 16, 16);
    const outerGlowMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffcc,
      transparent: true,
      opacity: 0.1,
      blending: THREE.AdditiveBlending
    });
    const outerGlow = new THREE.Mesh(outerGlowGeometry, outerGlowMaterial);
    glowGroup.add(outerGlow);
    
    glowGroup.position.copy(sun.position);
    return glowGroup;
  };
  
  const sunGlow = createSunGlow();
  scene.add(sunGlow);
  
  // Enhanced animation with realistic movement
  const animateSun = () => {
    const time = Date.now() * 0.001;
    
    // Gentle pulsing for sun glow
    sunGlow.children[0].scale.setScalar(1 + Math.sin(time * 1.2) * 0.08);
    sunGlow.children[1].scale.setScalar(1 + Math.sin(time * 0.8) * 0.05);
    
    // Animate each ray individually for realistic shimmer
    sunRays.children.forEach((child, index) => {
      if (child.isGroup) {
        // Handle dust motes
        child.children.forEach(dustMote => {
          if (dustMote.userData.velocity) {
            // Move dust motes
            dustMote.position.add(dustMote.userData.velocity);
            
            // Add slight bobbing motion
            dustMote.position.y += Math.sin(time * 2 + dustMote.userData.bobPhase) * 0.005;
            
            // Reset position if too far
            if (dustMote.position.y < -2) {
              dustMote.position.y = 8;
              dustMote.position.x = -4 + Math.random() * 8;
              dustMote.position.z = -4 + Math.random() * 8;
            }
            
            // Vary opacity
            dustMote.material.opacity = dustMote.userData.originalOpacity * 
              (0.7 + 0.3 * Math.sin(time * 3 + dustMote.userData.bobPhase));
          }
        });
      } else if (child.userData) {
        // Animate rays
        const rayData = child.userData;
        const rayTime = time * rayData.pulseSpeed + rayData.animationPhase;
        
        // Different animation styles for different ray types
        if (rayData.isGodRay) {
          // God rays pulse more dramatically
          child.material.opacity = rayData.originalOpacity * (0.6 + 0.4 * Math.sin(rayTime));
          // Subtle rotation
          child.rotation.z += Math.sin(time * 0.5 + rayData.animationPhase) * 0.002;
        } else if (rayData.isAtmosphere) {
          // Atmospheric rays shimmer gently
          child.material.opacity = rayData.originalOpacity * (0.8 + 0.2 * Math.sin(rayTime));
        } else {
          // Main rays have complex shimmer patterns
          child.material.opacity = rayData.originalOpacity * 
            (0.7 + 0.2 * Math.sin(rayTime) + 0.1 * Math.sin(rayTime * 2.3));
          
          // Add subtle movement to make rays feel alive
          child.rotation.z += Math.sin(time * 0.3 + rayData.animationPhase) * 0.001;
        }
      }
    });
    
    // Add slight movement to the sun itself
    sun.rotation.y += 0.002;
    
    requestAnimationFrame(animateSun);
  };
  
  animateSun();
  
  // Update directional light to match new sun position with enhanced properties
  const existingLights = scene.children.filter(child => child.isDirectionalLight);
  if (existingLights.length > 0) {
    const sunLight = existingLights[0];
    
    // Position light at new sun location
    sunLight.position.set(8, 12, -5);
    sunLight.target.position.set(0, 0, 0);
    sunLight.intensity = 1.3; // Slightly brighter
    sunLight.color.setHex(0xffffcc);
    
    // Enhanced shadow settings for more realistic shadows
    sunLight.castShadow = true;
    sunLight.shadow.mapSize.width = 2048;
    sunLight.shadow.mapSize.height = 2048;
    sunLight.shadow.camera.near = 5;
    sunLight.shadow.camera.far = 25;
    sunLight.shadow.camera.left = -18;
    sunLight.shadow.camera.right = 18;
    sunLight.shadow.camera.top = 18;
    sunLight.shadow.camera.bottom = -18;
    sunLight.shadow.bias = -0.0005; // Reduce shadow acne
    
    scene.add(sunLight.target);
  }
  
  return { sun, sunRays, sunGlow };
};


  const createCannabisPlant = (x, z, id, plantType = 'Blue Dream', stage = 0, health = 85) => {
  const plantGroup = new THREE.Group();
  const plant = plantTypes[plantType];
  
  // Get plant-specific visual characteristics

  const characteristics = getPlantCharacteristics(plantType);
  
  // Enhanced stem with plant-specific variations
  const createStem = () => {
    const stemGroup = new THREE.Group();
    const segments = 8;
    const stemHeight = (2 + (stage * 0.3)) * characteristics.height;
    
    for (let i = 0; i < segments; i++) {
      const segmentHeight = stemHeight / segments;
      const y = i * segmentHeight;
      
      // Natural tapering - thicker at bottom
      let bottomRadius = 0.12 - (i * 0.008);
      let topRadius = 0.10 - (i * 0.008);
      
      // Adjust thickness by plant type
      if (characteristics.specialFeatures.includes('sturdy')) {
        bottomRadius *= 1.3;
        topRadius *= 1.3;
      } else if (characteristics.specialFeatures.includes('tall')) {
        bottomRadius *= 0.8;
        topRadius *= 0.8;
      }
      
      const segmentGeometry = new THREE.CylinderGeometry(topRadius, bottomRadius, segmentHeight, 8);
      
      // Add slight color variation per segment
      const colorVariation = (Math.random() - 0.5) * 0.1;
      const stemMaterial = new THREE.MeshLambertMaterial({ 
        color: new THREE.Color(characteristics.stemColor).multiplyScalar(1 + colorVariation),
        roughness: 0.8
      });
      
      const segment = new THREE.Mesh(segmentGeometry, stemMaterial);
      segment.position.y = y + segmentHeight / 2;
      
      // Add slight random bend for natural look
      segment.rotation.x = (Math.random() - 0.5) * 0.05;
      segment.rotation.z = (Math.random() - 0.5) * 0.05;
      
      stemGroup.add(segment);
    }
    
    return stemGroup;
  };

  // Enhanced leaf creation with plant-specific characteristics
  const createLeafNode = (nodeHeight, nodeSize, plantHue) => {
    const nodeGroup = new THREE.Group();
    
    // Create 2-4 leaves per node (opposite pairs typical for cannabis)
    const leafCount = stage >= 2 ? 4 : 2;
    
    for (let i = 0; i < leafCount; i++) {
      const leaf = createCannabisLeafVariant(
        nodeSize * characteristics.leafSize, 
        plantHue, 
        health,
        characteristics.leafShape,
        characteristics.leafFingers,
        characteristics.specialFeatures
      );
      
      // Position leaves in cross pattern
      const angle = (i / leafCount) * Math.PI * 2;
      leaf.position.set(
        Math.cos(angle) * 0.3,
        0,
        Math.sin(angle) * 0.3
      );
      leaf.rotation.y = angle;
      
      // Add natural droop - varies by plant type
      let droopAmount = -0.1 - Math.random() * 0.2;
      if (characteristics.specialFeatures.includes('sturdy')) {
        droopAmount *= 0.5; // Less droop for sturdy plants
      }
      leaf.rotation.x = droopAmount;
      
      // Slight random variations
      leaf.rotation.z += (Math.random() - 0.5) * 0.3;
      
      nodeGroup.add(leaf);
    }
    
    nodeGroup.position.y = nodeHeight;
    return nodeGroup;
  };

  // Enhanced bud creation with strain-specific characteristics
  const createBud = (size, budHue) => {
    const budGroup = new THREE.Group();
    
    // Adjust bud count based on strain density
    const baseCalyxCount = 8 + Math.floor(Math.random() * 6);
    const calyxCount = Math.floor(baseCalyxCount * characteristics.budDensity);
    
    for (let i = 0; i < calyxCount; i++) {
      let calyxSize = 0.03 + Math.random() * 0.02;
      
      // Strain-specific bud sizing
      if (characteristics.specialFeatures.includes('dense_buds')) {
        calyxSize *= 1.3;
      } else if (characteristics.specialFeatures.includes('compact')) {
        calyxSize *= 0.8;
      }
      
      const calyxGeometry = new THREE.SphereGeometry(calyxSize, 6, 4);
      
      let budColor = new THREE.Color().setHSL(
        budHue + (Math.random() - 0.5) * 0.1,
        0.6 + Math.random() * 0.3,
        0.4 + Math.random() * 0.2
      );
      
      // Special color effects for certain strains
      if (characteristics.specialFeatures.includes('golden_buds')) {
        budColor = new THREE.Color().setHSL(0.08, 0.8, 0.6);
      } else if (characteristics.specialFeatures.includes('white_crystals')) {
        budColor.lerp(new THREE.Color(0xffffff), 0.3);
      }
      
      const calyxMaterial = new THREE.MeshLambertMaterial({
        color: budColor
      });
      
      const calyx = new THREE.Mesh(calyxGeometry, calyxMaterial);
      
      // Cluster calyxes naturally
      const clusterRadius = size * 0.8;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      
      calyx.position.set(
        Math.sin(phi) * Math.cos(theta) * clusterRadius,
        Math.random() * size * 0.5,
        Math.sin(phi) * Math.sin(theta) * clusterRadius
      );
      
      budGroup.add(calyx);
    }
    
    // Add pistils (orange/white hairs) with strain variations
    if (stage >= 2) {
      let pistilCount = 15 + Math.floor(Math.random() * 10);
      
      // Adjust pistil count by strain
      if (characteristics.specialFeatures.includes('dense_buds')) {
        pistilCount *= 1.5;
      }
      
      for (let i = 0; i < pistilCount; i++) {
        const pistilGeometry = new THREE.CylinderGeometry(0.001, 0.001, 0.15, 3);
        
        let pistilColor = Math.random() > 0.5 ? 0xffa500 : 0xffffff;
        
        // Strain-specific pistil colors
        if (characteristics.specialFeatures.includes('purple_tips')) {
          pistilColor = 0xbb44bb;
        } else if (characteristics.specialFeatures.includes('golden_buds')) {
          pistilColor = 0xffdd00;
        }
        
        const pistilMaterial = new THREE.MeshBasicMaterial({
          color: pistilColor,
          transparent: true,
          opacity: 0.8
        });
        
        const pistil = new THREE.Mesh(pistilGeometry, pistilMaterial);
        
        // Random positioning emerging from bud
        const angle = Math.random() * Math.PI * 2;
        const distance = Math.random() * size;
        
        pistil.position.set(
          Math.cos(angle) * distance,
          Math.random() * size * 0.3,
          Math.sin(angle) * distance
        );
        
        pistil.rotation.x = (Math.random() - 0.5) * 0.5;
        pistil.rotation.z = (Math.random() - 0.5) * 0.5;
        
        budGroup.add(pistil);
      }
    }
    
    // Add special visual effects
    if (characteristics.specialFeatures.includes('frost')) {
      addFrostEffect(budGroup, size);
    }
    
    if (characteristics.specialFeatures.includes('diesel_shimmer')) {
      addShimmerEffect(budGroup);
    }
    
    return budGroup;
  };

  // Build the plant from bottom to top with strain characteristics
  const stem = createStem();
  plantGroup.add(stem);
  
  // Add leaf nodes at different heights
  const nodeCount = 3 + stage;
  const plantHeight = (2 + (stage * 0.3)) * characteristics.height;
  
  for (let i = 0; i < nodeCount; i++) {
    const nodeHeight = (plantHeight / nodeCount) * (i + 1);
    const nodeSize = 0.8 - (i * 0.15); // Smaller leaves toward top
    
    const leafNode = createLeafNode(nodeHeight, nodeSize, characteristics.leafHue);
    plantGroup.add(leafNode);
  }
  
  // Add buds for mature plants
  if (stage >= 1) {
    const budCount = Math.min(stage + 1, 6);
    
    for (let i = 0; i < budCount; i++) {
      const budSize = (0.08 + (stage * 0.02) + Math.random() * 0.03) * characteristics.budDensity;
      const bud = createBud(budSize, characteristics.budHue);
      
      // Position buds at leaf nodes and top
      const budHeight = plantHeight * 0.6 + (i * 0.2);
      const angle = (i / budCount) * Math.PI * 2;
      
      bud.position.set(
        Math.cos(angle) * (0.2 + Math.random() * 0.1),
        budHeight,
        Math.sin(angle) * (0.2 + Math.random() * 0.1)
      );
      
      plantGroup.add(bud);
    }
  }
  
  // Add special plant effects
  if (characteristics.specialFeatures.includes('haze_effect')) {
    addHazeEffect(plantGroup);
  }
  
  // Add subtle animation/movement
  plantGroup.userData.originalPosition = { x, z };
  plantGroup.userData.animationOffset = Math.random() * Math.PI * 2;
  
  // Final positioning and scaling
  plantGroup.position.set(x, 0.8, z);
  plantGroup.userData = { 
    type: 'plant', 
    id: id, 
    plantType: plantType, 
    stage: stage 
  };
  plantGroup.castShadow = true;
  plantGroup.receiveShadow = true;
  
  // Health-based scaling with strain-specific proportions
  const healthScale = (health / 100) * 0.3 + 0.7;
  const stageScale = 0.6 + (stage * 0.15);
  const strainScale = characteristics.height;
  const finalScale = healthScale * stageScale * strainScale;
  
  plantGroup.scale.set(finalScale, finalScale, finalScale);
  
  return plantGroup;
};

// Enhanced leaf creation function with strain variations
const createCannabisLeafVariant = (size = 1, hue = 0.25, health, leafShape, fingerCount, specialFeatures) => {
  const leafGroup = new THREE.Group();
  
  // Adjust finger count based on strain
  const actualFingerCount = fingerCount || (Math.floor(Math.random() * 3) + 5);
  
  for (let i = 0; i < actualFingerCount; i++) {
    const fingerGeometry = new THREE.BufferGeometry();
    
    // Adjust finger length based on leaf shape
    let fingerLength = (0.6 + Math.random() * 0.4) * size;
    let fingerWidth = (0.15 + Math.random() * 0.1) * size;
    
    switch (leafShape) {
      case 'broad':
        fingerWidth *= 1.4;
        fingerLength *= 0.9;
        break;
      case 'narrow':
        fingerWidth *= 0.7;
        fingerLength *= 1.2;
        break;
      case 'elongated':
        fingerWidth *= 0.8;
        fingerLength *= 1.4;
        break;
      case 'spiky':
        fingerWidth *= 0.6;
        fingerLength *= 1.1;
        break;
      case 'tropical':
        fingerWidth *= 1.1;
        fingerLength *= 1.1;
        break;
    }
    
    const vertices = [];
    const uvs = [];
    const indices = [];
    
    const segments = leafShape === 'serrated' ? 16 : 12;
    
    for (let j = 0; j <= segments; j++) {
      const t = j / segments;
      const y = t * fingerLength;
      
      let leftX = -fingerWidth * (1 - t * 0.8) * Math.sin(Math.PI * t);
      let rightX = fingerWidth * (1 - t * 0.8) * Math.sin(Math.PI * t);
      
      // Enhanced serrations for certain leaf shapes
      if (j > 0 && j < segments) {
        let serration = Math.sin(j * Math.PI * 3) * 0.02 * size;
        
        if (leafShape === 'serrated') {
          serration *= 2;
        } else if (leafShape === 'spiky') {
          serration *= 1.5;
        }
        
        leftX += serration;
        rightX += serration;
      }
      
      vertices.push(leftX, y, 0);
      uvs.push(0, t);
      vertices.push(rightX, y, 0);
      uvs.push(1, t);
      
      if (j < segments) {
        const base = j * 2;
        indices.push(base, base + 1, base + 2);
        indices.push(base + 1, base + 3, base + 2);
      }
    }
    
    fingerGeometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    fingerGeometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    fingerGeometry.setIndex(indices);
    fingerGeometry.computeVertexNormals();
    
    // Create leaf material with strain-specific properties
    let leafColor = new THREE.Color().setHSL(
      hue + (Math.random() - 0.5) * 0.05,
      0.7 + Math.random() * 0.2,
      0.3 + Math.random() * 0.3 + (health / 200)
    );
    
    // Apply special color effects
    if (specialFeatures.includes('purple_leaves')) {
      leafColor.lerp(new THREE.Color(0x663366), 0.4);
    } else if (specialFeatures.includes('blue_tint')) {
      leafColor.lerp(new THREE.Color(0x336666), 0.2);
    } else if (specialFeatures.includes('bright_green')) {
      leafColor.multiplyScalar(1.2);
    } else if (specialFeatures.includes('yellow_tint')) {
      leafColor.lerp(new THREE.Color(0x666633), 0.2);
    } else if (specialFeatures.includes('rich_green')) {
      // White Widow - rich, vibrant green with slight brightness boost
      leafColor.setHSL(0.25, 0.85, 0.45); // Richer, more saturated green
      leafColor.multiplyScalar(1.15); // Slight brightness boost
    } else if (specialFeatures.includes('deep_green')) {
      // Northern Lights - deep, forest green
      leafColor.setHSL(0.28, 0.8, 0.35); // Deep forest green
      leafColor.multiplyScalar(1.05); // Subtle brightness to avoid too dark
    }
    const leafMaterial = new THREE.MeshLambertMaterial({
      color: leafColor,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.9
    });
    
    const finger = new THREE.Mesh(fingerGeometry, leafMaterial);
    
    // Position fingers in cannabis leaf pattern
    const centerIndex = Math.floor(actualFingerCount / 2);
    const angle = (i - centerIndex) * 0.3;
    const fingerScale = i === centerIndex ? 1 : 0.7 + Math.abs(i - centerIndex) * 0.1;
    
    finger.scale.set(fingerScale, fingerScale, 1);
    finger.rotation.z = angle;
    finger.position.x = Math.sin(angle) * 0.1 * size;
    
    leafGroup.add(finger);
  }
  
  return leafGroup;
};

// Special effect functions
const addFrostEffect = (budGroup, size) => {
  const frostCount = 20;
  
  for (let i = 0; i < frostCount; i++) {
    const frostGeometry = new THREE.SphereGeometry(0.005, 4, 4);
    const frostMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.6
    });
    
    const frost = new THREE.Mesh(frostGeometry, frostMaterial);
    
    frost.position.set(
      (Math.random() - 0.5) * size * 2,
      Math.random() * size,
      (Math.random() - 0.5) * size * 2
    );
    
    budGroup.add(frost);
  }
};

  const handleCloseShop = () => {
    setShowShop(false);
  };

const addShimmerEffect = (budGroup) => {
  // Add subtle shimmer particles
  const shimmerCount = 10;
  
  for (let i = 0; i < shimmerCount; i++) {
    const shimmerGeometry = new THREE.SphereGeometry(0.002, 3, 3);
    const shimmerMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffaa,
      transparent: true,
      opacity: 0.3,
      blending: THREE.AdditiveBlending
    });
    
    const shimmer = new THREE.Mesh(shimmerGeometry, shimmerMaterial);
    
    shimmer.position.set(
      (Math.random() - 0.5) * 0.3,
      Math.random() * 0.2,
      (Math.random() - 0.5) * 0.3
    );
    
    budGroup.add(shimmer);
  }
};

const addHazeEffect = (plantGroup) => {
  // Add a subtle misty effect around Purple Haze
  const hazeGeometry = new THREE.SphereGeometry(0.8, 8, 8);
  const hazeMaterial = new THREE.MeshBasicMaterial({
    color: 0x9966cc,
    transparent: true,
    opacity: 0.05,
    blending: THREE.AdditiveBlending
  });
  
  const haze = new THREE.Mesh(hazeGeometry, hazeMaterial);
  haze.position.y = 1.5;
  
  plantGroup.add(haze);
};

const getUnlockedPositionsByLevel = (level) => {
  if (level >= 30) return 20;  // Tier 4 (Max Level)
  if (level >= 20) return 15;  // Tier 3
  if (level >= 10) return 10;  // Tier 2
  return 5;                    // Tier 1 (default)
};

const createGrowRoom = async () => {
    try {
      const response = await GrowRoomAPI.createGrowRoom();
      
      if (response.success) {
        setHasGrowRoom(true);
        setShowCreateGrowRoom(false);
        addNotification(response.message);
        await loadGrowRoomData();
      } else {
        addNotification(`❌ ${response.error}`);
      }
    } catch (error) {
      console.error('Error creating grow room:', error);
      addNotification('❌ Failed to create grow room');
    }
  };

  const soilRef = useRef([]);


  const createGarden = (gameData = null) => {
  if (!sceneRef.current) return;

  console.log('Creating garden with backend pot positions:', gameData);

  // Clear existing
  plantsRef.current.forEach(plant => {
    if (plant) sceneRef.current.remove(plant);
  });
  potsRef.current.forEach(pot => {
    if (pot) sceneRef.current.remove(pot);
  });
  soilRef.current.forEach(soil => {
    if (soil) sceneRef.current.remove(soil);
  });

  plantsRef.current = [];
  potsRef.current = [];
  soilRef.current = [];

  // 🔧 NEW: Use backend pot positions if available
  const backendPots = gameData?.pots || [];
  
  // Get how many POTS user has unlocked
  const level = userStats?.level || 1;
  const unlockedPotCount = level >= 30 ? 19 : 
                          level >= 20 ? 15 : 
                          level >= 10 ? 10 : 5;
  
  console.log(`Level: ${level}, Available Pots: ${unlockedPotCount}`);

  // Create pots using backend positions or defaults
  for (let potIndex = 0; potIndex < unlockedPotCount; potIndex++) {
    // 🔧 FIXED: Use backend pot position data
    const backendPot = backendPots.find(p => p && p.position_index === potIndex);

    const pos = backendPot ? 
      [backendPot.position_x, backendPot.position_z] : 
      allGridPositions[potIndex] || allGridPositions[0];
    
    console.log(`Creating pot ${potIndex} at position:`, pos);
    
    // Create pot visual
    const potGeometry = new THREE.CylinderGeometry(0.8, 0.6, 0.8, 8);
    const potMaterial = new THREE.MeshLambertMaterial({ color: 0x4A4A4A });
    const pot = new THREE.Mesh(potGeometry, potMaterial);
    pot.position.set(pos[0], 0.4, pos[1]);
    pot.castShadow = true;
    pot.userData = { type: 'pot', id: potIndex };
    sceneRef.current.add(pot);
    potsRef.current.push(pot);
    
    // Soil
    const soilGeometry = new THREE.CylinderGeometry(0.7, 0.7, 0.1, 8);
    const soilMaterial = new THREE.MeshLambertMaterial({ color: 0x8B4513 });
    const soil = new THREE.Mesh(soilGeometry, soilMaterial);
    soil.position.set(pos[0], 0.8, pos[1]);
    soil.userData = { type: 'soil', potIndex: potIndex };
    sceneRef.current.add(soil);
    soilRef.current.push(soil);
    
    // Create plant if exists
    const plantData = gameData?.plants?.[potIndex] || gameState.plants[potIndex];
    if (plantData && !plantData.isRemoved) {
      console.log(`Creating plant in pot ${potIndex}:`, plantData);
      
      const plant = createCannabisPlant(
        pos[0], 
        pos[1], 
        potIndex,
        plantData.plantType || 'Blue Dream', 
        plantData.stage || 0, 
        plantData.health || 85
      );
      
      sceneRef.current.add(plant);
      plantsRef.current.push(plant);
    } else {
      plantsRef.current.push(null);
    }
  }
  
  console.log(`Garden created with ${unlockedPotCount} pots using backend positions!`);
};


  const plantSelectedSeed = async (plantType, potId) => {
    const plantData = gameState.plants[potId];
    const plant = plantTypes[plantType];
    
    if (!plantData || !plantData.isRemoved) {
      addNotification('🌱 This pot already has a plant!');
      return;
    }
    
    if (gameState.inventory[plantType] <= 0) {
      addNotification(`🌱 No ${plantType} seeds in inventory!`);
      return;
    }

    try {
      // 🔧 NEW: Call backend API to plant the seed
      const plantResponse = await GrowRoomAPI.plantSeed(potId, plantType);
      
      if (!plantResponse.success) {
        addNotification(`❌ ${plantResponse.error}`);
        return;
      }

      // Get the planted plant data from backend response
      const backendPlantData = plantResponse.plantData;
      
      const positions = [
        [-6, -4], [-3, -4], [0, -4], [3, -4], [6, -4],
        [-6, -1], [-3, -1], [0, -1], [3, -1], [6, -1],
        [-6, 2], [-3, 2], [0, 2], [3, 2], [6, 2],
        [-4, 5], [-1, 5], [2, 5], [5, 5]
      ];

      const pos = positions[potId];
      if (!pos) return;

      // Create visual plant using backend data
      const newPlant = createCannabisPlant(
        pos[0], 
        pos[1], 
        potId, 
        backendPlantData.plant_type, 
        backendPlantData.stage, 
        backendPlantData.health
      );
      
      newPlant.scale.set(0.1, 0.1, 0.1);
      sceneRef.current.add(newPlant);
      plantsRef.current[potId] = newPlant;

      // Animate growth
      const animateGrowth = () => {
        let scale = 0.1;
        const targetScale = (backendPlantData.health / 100) * 0.8 + 0.2;
        
        const growUp = () => {
          scale += 0.03;
          newPlant.scale.set(scale, scale, scale);
          
          if (scale < targetScale) {
            requestAnimationFrame(growUp);
          }
        };
        
        growUp();
      };

      // 🔧 FIX: Update game state with backend data
      setGameState(prev => {
        const newState = { ...prev };
        
        // Update inventory from backend response
        newState.inventory = plantResponse.inventory;
        
        // Update plant state with backend data
        newState.plants[potId] = {
          id: potId,
          plantType: backendPlantData.plant_type,
          stage: backendPlantData.stage,
          health: backendPlantData.health,
          waterLevel: backendPlantData.water_level,
          lightLevel: backendPlantData.light_level,
          soilQuality: backendPlantData.soil_quality,
          lastWatered: backendPlantData.last_watered,
          growth: backendPlantData.growth_progress,
          needsAttention: backendPlantData.needs_attention,
          isRemoved: false
        };
        
        return newState;
      });

      animateGrowth();
      addNotification(`🌱 ${plantType} planted successfully!`);
      setShowPlantInventory(null);
      setSelectedPotId(null);

    } catch (error) {
      console.error('Error planting seed:', error);
      addNotification('❌ Failed to plant seed');
    }
  };

  const removePlant = async (plantId) => {
  const plant = plantsRef.current[plantId];
  const plantData = gameState.plants[plantId];
  
  if (!plant || !plantData || plantData.isRemoved) return;

  try {
    const removalPoints = Math.floor(plantData.health * 0.5 + plantData.growth * 20);
    
    // 🔧 NEW: Call backend API for plant removal
    const removeResponse = await GrowRoomAPI.removePlant(plantId, removalPoints);
    
    if (!removeResponse.success) {
      addNotification(`❌ ${removeResponse.error}`);
      return;
    }

    const animateRemoval = () => {
      let opacity = 1;
      let scale = 1;
      
      const fadeOut = () => {
        opacity -= 0.05;
        scale -= 0.05;
        
        plant.traverse((child) => {
          if (child.material) {
            child.material.transparent = true;
            child.material.opacity = opacity;
          }
        });
        
        plant.scale.multiplyScalar(scale > 0 ? scale : 0.01);
        
        if (opacity > 0) {
          requestAnimationFrame(fadeOut);
        } else {
          sceneRef.current.remove(plant);
          plantsRef.current[plantId] = null;
        }
      };
      
      fadeOut();
    };

    // Update game state with backend response
    setGameState(prev => ({
      ...prev,
      points: removeResponse.newPoints,
      inventory: removeResponse.inventory,
      plants: prev.plants.map((plant, index) => 
        index === plantId ? { ...plant, isRemoved: true } : plant
      )
    }));

    animateRemoval();
    addNotification(`🗑️ ${plantData.plantType} removed! +${removalPoints} points`);
    
    if (removeResponse.seedsRecovered > 0) {
      addNotification(`🌰 Got a ${plantData.plantType} seed back!`);
    }

    setShowRemoveConfirm(null);

  } catch (error) {
    console.error('Error removing plant:', error);
    addNotification('❌ Failed to remove plant');
  }
};

  const addNotification = (message) => {
    const id = Date.now() + Math.random();
    
    // Check for duplicate/similar messages in last 500ms
    const now = Date.now();
    const recentNotifications = notifications.filter(n => (now - (n.timestamp || 0)) < 500);
    
    // If this is a repeated action message, don't spam
    const actionPatterns = [
      /💧.*watered/i,
      /🥄.*soil/i, 
      /💡.*lighting/i,
      /🧪.*fertilize/i,
      /already.*optimal/i,
      /already.*healthy/i
    ];
    
    const isActionMessage = actionPatterns.some(pattern => pattern.test(message));
    const hasRecentSimilar = recentNotifications.some(n => 
      actionPatterns.some(pattern => pattern.test(n.message) && pattern.test(message))
    );
    
    // For action messages, only show if no recent similar message
    if (isActionMessage && hasRecentSimilar) {
      return; // Skip this notification
    }
    
    // For important messages (errors, harvests, etc.) always show
    const isImportant = message.includes('❌') || message.includes('✂️') || 
                      message.includes('🌱 planted') || message.includes('points');
    
    if (!isImportant && isActionMessage) {
      // Limit action notifications to max 3 on screen
      const actionNotifications = notifications.filter(n => 
        actionPatterns.some(pattern => pattern.test(n.message))
      );
      
      if (actionNotifications.length >= 3) {
        // Remove oldest action notification
        setNotifications(prev => {
          const filtered = prev.filter(n => n.id !== actionNotifications[0].id);
          return [...filtered, { id, message, timestamp: now }];
        });
        return;
      }
    }
    
    setNotifications(prev => [...prev, { id, message, timestamp: now }]);
    
    // Auto-remove after 3 seconds (2 seconds for action messages)
    const removeDelay = isActionMessage ? 2000 : 3000;
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, removeDelay);
  };

  const [actionCooldowns, setActionCooldowns] = useState({});

  const handleShopPurchase = async (itemName, itemType, price, quantity) => {
    try {
      let response;
      
      if (itemType === 'seed') {
        response = await GrowRoomAPI.purchaseSeed(itemName, quantity);
      } else if (itemType === 'bundle') {
        response = await GrowRoomAPI.purchaseBundle(itemName);
      } else {
        addNotification('❌ Invalid purchase type');
        return;
      }
      
      if (response.success) {
        // Update game state with new points and inventory  
        setGameState(prev => ({
          ...prev,
          points: response.new_points,
          inventory: response.inventory
        }));
        
        // Show success message
        if (itemType === 'seed') {
          addNotification(`🛒 Purchased ${quantity}x ${itemName} for ${price} points!`);
          
          // Show rarity-specific message
          if (response.purchase_details?.rarity) {
            const rarityMessages = {
              'Common': `🌱 Great choice for reliable growing!`,
              'Uncommon': `🔸 Quality genetics acquired!`,
              'Rare': `🔹 Premium strain added to collection!`,
              'Legendary': `🟨 Legendary genetics secured!`,
              'Exotic': `🟣 Ultra-rare strain obtained!`
            };
            
            const rarityMsg = rarityMessages[response.purchase_details.rarity];
            if (rarityMsg) {
              setTimeout(() => addNotification(rarityMsg), 1000);
            }
          }
        } else if (itemType === 'bundle') {
          addNotification(`📦 ${itemName} bundle purchased!`);
          addNotification(`💰 Saved ${response.bundle_details.savings} points!`);
          
          // Show what was included
          const items = Object.entries(response.bundle_details.items)
            .map(([name, qty]) => `${qty}x ${name}`)
            .join(', ');
          setTimeout(() => addNotification(`📋 Received: ${items}`), 1500);
        }
        
        // Add to purchase history
        setPurchaseHistory(prev => [response.purchase_details || response.bundle_details, ...prev]);
        
      } else {
        addNotification(`❌ ${response.error}`);
      }
      
    } catch (error) {
      console.error('Error making purchase:', error);
      addNotification('❌ Purchase failed - please try again');
    }
  };

  const selectTool = (tool) => {
    if (tool === 'inventory') {
      setShowInventory(true);
      return;
    }

    // 🔧 ADD: Handle shop tool
    if (tool === 'shop') {
      setShowShop(true);
      loadShopCatalog();
      return;
    }

    setGameState(prev => ({
      ...prev,
      activeTool: prev.activeTool === tool ? null : tool
    }));
  };
  const getNotificationStyle = (message) => {
  if (message.includes('❌')) {
    return {
      gradient: 'rgba(239, 68, 68, 0.95) 0%, rgba(220, 38, 38, 0.98) 100%',
      borderColor: 'rgba(248, 113, 113, 0.3)',
      accentColor: '#dc2626',
      shadowColor: 'rgba(239, 68, 68, 0.25)',
      glowColor: '#fca5a5',
      textColor: 'white',
      icon: '❌'
    };
  } else if (message.includes('💧')) {
    return {
      gradient: 'rgba(59, 130, 246, 0.95) 0%, rgba(37, 99, 235, 0.98) 100%',
      borderColor: 'rgba(147, 197, 253, 0.3)',
      accentColor: '#2563eb',
      shadowColor: 'rgba(59, 130, 246, 0.25)',
      glowColor: '#93c5fd',
      textColor: 'white',
      icon: '💧'
    };
  } else if (message.includes('💡')) {
    return {
      gradient: 'rgba(251, 191, 36, 0.95) 0%, rgba(245, 158, 11, 0.98) 100%',
      borderColor: 'rgba(252, 211, 77, 0.3)',
      accentColor: '#f59e0b',
      shadowColor: 'rgba(251, 191, 36, 0.25)',
      glowColor: '#fcd34d',
      textColor: 'rgba(0,0,0,0.9)',
      icon: '💡'
    };
  } else if (message.includes('🧪')) {
    return {
      gradient: 'rgba(139, 92, 246, 0.95) 0%, rgba(124, 58, 237, 0.98) 100%',
      borderColor: 'rgba(196, 181, 253, 0.3)',
      accentColor: '#7c3aed',
      shadowColor: 'rgba(139, 92, 246, 0.25)',
      glowColor: '#c4b5fd',
      textColor: 'white',
      icon: '🧪'
    };
  } else if (message.includes('🥄')) {
    return {
      gradient: 'rgba(101, 163, 13, 0.95) 0%, rgba(77, 124, 15, 0.98) 100%',
      borderColor: 'rgba(163, 230, 53, 0.3)',
      accentColor: '#4d7c0f',
      shadowColor: 'rgba(101, 163, 13, 0.25)',
      glowColor: '#a3e635',
      textColor: 'white',
      icon: '🥄'
    };
  } else if (message.includes('✂️')) {
    return {
      gradient: 'rgba(16, 185, 129, 0.95) 0%, rgba(5, 150, 105, 0.98) 100%',
      borderColor: 'rgba(110, 231, 183, 0.3)',
      accentColor: '#059669',
      shadowColor: 'rgba(16, 185, 129, 0.25)',
      glowColor: '#6ee7b7',
      textColor: 'white',
      icon: '✂️'
    };
  } else if (message.includes('🌱')) {
    return {
      gradient: 'rgba(34, 197, 94, 0.95) 0%, rgba(22, 163, 74, 0.98) 100%',
      borderColor: 'rgba(134, 239, 172, 0.3)',
      accentColor: '#16a34a',
      shadowColor: 'rgba(34, 197, 94, 0.25)',
      glowColor: '#86efac',
      textColor: 'white',
      icon: '🌱'
    };
  } else if (message.includes('🛒') || message.includes('📦')) {
    return {
      gradient: 'rgba(139, 92, 246, 0.95) 0%, rgba(124, 58, 237, 0.98) 100%',
      borderColor: 'rgba(196, 181, 253, 0.3)',
      accentColor: '#7c3aed',
      shadowColor: 'rgba(139, 92, 246, 0.25)',
      glowColor: '#c4b5fd',
      textColor: 'white',
      icon: '🛒'
    };
  } else {
    // Default success style
    return {
      gradient: 'rgba(16, 185, 129, 0.95) 0%, rgba(5, 150, 105, 0.98) 100%',
      borderColor: 'rgba(110, 231, 183, 0.3)',
      accentColor: '#059669',
      shadowColor: 'rgba(16, 185, 129, 0.25)',
      glowColor: '#6ee7b7',
      textColor: 'white',
      icon: '✅'
    };
  }
};



     const loadShopCatalog = async () => {
      if (shopCatalog) return; // Already loaded
      
      try {
        setShopLoading(true);
        const response = await GrowRoomAPI.getShopCatalog();
        
        if (response.success) {
          setShopCatalog(response);
        } else {
          addNotification('❌ Failed to load shop catalog');
        }
      } catch (error) {
        console.error('Error loading shop catalog:', error);
        addNotification('❌ Shop temporarily unavailable');
      } finally {
        setShopLoading(false);
      }
    };

  const performAction = async (tool, plantId) => {
    // Check cooldown for this specific plant+tool combo
    const cooldownKey = `${tool}-${plantId}`;
    const now = Date.now();
    const lastAction = actionCooldowns[cooldownKey] || 0;
    const cooldownTime = 100; // 100ms cooldown between same actions
    
    if (now - lastAction < cooldownTime) {
      return; // Skip if too soon
    }
    
    // Update cooldown
    setActionCooldowns(prev => ({
      ...prev,
      [cooldownKey]: now
    }));
  const plantData = gameState.plants[plantId];
  
  if (tool === 'remove') {
    if (!plantData || plantData.isRemoved) {
      addNotification('🗑️ No plant to remove in this pot!');
      return;
    }
    
    setShowRemoveConfirm({
      plantId,
      strain: plantData.plantType,
      health: plantData.health,
      points: Math.floor(plantData.health * 0.5 + plantData.growth * 20)
    });
    return;
  }

  if (tool === 'plant') {
    if (!plantData || !plantData.isRemoved) {
      addNotification('🌱 This pot already has a plant!');
      return;
    }
    
    setSelectedPotId(plantId);
    setShowPlantInventory(true);
    return;
  }

  // For harvest, also call backend
  if (tool === 'harvest') {
    if (!plantData || plantData.isRemoved) {
      addNotification('⚠️ No plant in this pot to harvest!');
      return;
    }

    const characteristics = getPlantCharacteristics(plantData.plantType);
    console.log(`s TO s ${plantData.stage}`)
    console.log(`s TO s ${characteristics.stageThresholds}`)
    console.log('==========================================')
    let currentStageThreshold = characteristics.stageThresholds[plantData.stage];
    if (currentStageThreshold == undefined) {
      currentStageThreshold = characteristics.stageThresholds[plantData.stage - 1];
    }
    const isReadyToHarvest = plantData.health > 80 && plantData.growth > currentStageThreshold;
    
    console.log(`READYZ TO HARVEST ${isReadyToHarvest}`)
    console.log(`READYZ TO HARVEST ${plantData.growth}`)
    console.log(`READYZ TO HARVEST ${currentStageThreshold}`)
    if (isReadyToHarvest) {
      try {
        // Calculate harvest details
        let harvestBonus = plantTypes[plantData.plantType].rarity === 'Legendary' ? 3 : 
                          plantTypes[plantData.plantType].rarity === 'Rare' ? 2 : 
                          plantTypes[plantData.plantType].rarity === 'Uncommon' ? 1.5 : 1;
        
        const isPerfectWater = Math.abs(plantData.waterLevel - characteristics.idealWater) <= 5;
        const isPerfectLight = Math.abs(plantData.lightLevel - characteristics.idealLight) <= 5;
        const isPerfectSoil = Math.abs(plantData.soilQuality - characteristics.idealSoil) <= 5;
        
        if (isPerfectWater && isPerfectLight && isPerfectSoil) {
          harvestBonus *= 1.5;
        }
        
        if (characteristics.traits.includes('high_yield')) {
          harvestBonus *= 1.3;
        }
        
        const harvestPoints = Math.floor(50 * harvestBonus);
        
        // Calculate seeds earned
        const guaranteedSeeds = plantTypes[plantData.plantType].rarity === 'Legendary' ? 2 :
                               plantTypes[plantData.plantType].rarity === 'Rare' ? 1 :
                               plantTypes[plantData.plantType].rarity === 'Uncommon' ? 1 : 1;
        
        let bonusSeeds = 0;
        if (isPerfectWater && isPerfectLight && isPerfectSoil && Math.random() < 0.3) {
          bonusSeeds = 1;
        }
        
        const totalSeeds = { [plantData.plantType]: guaranteedSeeds + bonusSeeds };

        // 🔧 NEW: Call backend API for harvest
        const harvestResponse = await GrowRoomAPI.harvestPlant(plantId, harvestPoints, totalSeeds);
        
        if (!harvestResponse.success) {
          addNotification(`❌ ${harvestResponse.error}`);
          return;
        }

        // Update game state with backend response
        setGameState(prev => ({
          ...prev,
          points: harvestResponse.newPoints,
          totalHarvests: harvestResponse.totalHarvests,
          inventory: harvestResponse.inventory,
          plants: prev.plants.map((plant, index) => 
            index === plantId ? { ...plant, isRemoved: true } : plant
          )
        }));

        // Animate plant removal
        const plantVisual = plantsRef.current[plantId];
        if (plantVisual) {
          const animateHarvest = () => {
            let opacity = 1;
            let scale = 1;
            
            const fadeOut = () => {
              opacity -= 0.08;
              scale -= 0.08;
              
              plantVisual.traverse((child) => {
                if (child.material) {
                  child.material.transparent = true;
                  child.material.opacity = opacity;
                }
              });
              
              plantVisual.scale.multiplyScalar(scale > 0 ? scale : 0.01);
              
              if (opacity > 0) {
                requestAnimationFrame(fadeOut);
              } else {
                sceneRef.current.remove(plantVisual);
                plantsRef.current[plantId] = null;
              }
            };
            
            fadeOut();
          };
          
          animateHarvest();
        }

        const harvestMessages = {
          'Sour Diesel': `🔥 Legendary ${plantData.plantType} harvest! The wait was worth it! +${harvestPoints} points`,
          'White Widow': `❄️ Premium ${plantData.plantType} crystals harvested! +${harvestPoints} points`,
          'Green Crack': `⚡ Quick ${plantData.plantType} harvest ready! +${harvestPoints} points`,
          'Purple Haze': `🟣 Beautiful ${plantData.plantType} with stunning colors! +${harvestPoints} points`,
          'Northern Lights': `🌟 Hardy ${plantData.plantType} provides reliable yield! +${harvestPoints} points`,
          'Pineapple Express': `🍍 Tropical ${plantData.plantType} harvest! +${harvestPoints} points`
        };
        
        const message = harvestMessages[plantData.plantType] || 
                       `✂️ Harvested ${plantData.plantType}! +${harvestPoints} points`;
        
        addNotification(message);
        addNotification(`✂️ Harvest #${harvestResponse.totalHarvests} complete! +${guaranteedSeeds + bonusSeeds} seeds`);

        if (bonusSeeds > 0) {
          addNotification(`🌰 Perfect harvest yielded bonus ${plantData.plantType} seeds!`);
        }

      } catch (error) {
        console.error('Error harvesting plant:', error);
        addNotification('❌ Failed to harvest plant');
      }
    } else {
      const neededGrowth = Math.round((currentStageThreshold - plantData.growth) * 100);
      addNotification(`✂️ ${plantData.plantType} not ready yet. Needs ${neededGrowth}% more growth.`);
    }
    
    return;
  }

  // For other tools (water, rake, light, fertilize), call backend update
  if (['water', 'rake', 'light', 'fertilize'].includes(tool)) {
    if (!plantData || plantData.isRemoved) {
      addNotification('⚠️ No plant in this pot to interact with!');
      return;
    }

    try {
      // Calculate the updates locally first (same logic as before)
      const effectiveness = getToolEffectiveness(tool, plantData.plantType);
      const characteristics = getPlantCharacteristics(plantData.plantType);
      
      let updates = {};
      let message = '';
      let points = 0;

      switch (tool) {
        case 'water':
          const waterAmount = Math.floor(effectiveness.baseAmount * effectiveness.multiplier);
          const maxWaterLevel = characteristics.idealWater + 20;
          
          if (plantData.waterLevel < maxWaterLevel) {
            updates.waterLevel = Math.min(maxWaterLevel, plantData.waterLevel + waterAmount);
            updates.lastWatered = Date.now();
            points = Math.floor(5 * effectiveness.multiplier);
            
            if (characteristics.waterNeeds === 'high') {
              message = `💧 Your thirsty ${plantData.plantType} drinks deeply! +${waterAmount}%`;
            } else if (characteristics.waterNeeds === 'low') {
              message = `💧 Your hardy ${plantData.plantType} needs just a sip! +${waterAmount}%`;
            } else {
              message = `💧 ${plantData.plantType} watered perfectly! +${waterAmount}%`;
            }
          } else {
            message = characteristics.waterNeeds === 'low' ? 
                     `💧 Your ${plantData.plantType} prefers to stay dry!` : 
                     `💧 This plant doesn't need water yet.`;
          }
          break;
          
        case 'rake':
          const soilAmount = Math.floor(effectiveness.baseAmount * effectiveness.multiplier);
          
          if (plantData.soilQuality < 90) {
            updates.soilQuality = Math.min(100, plantData.soilQuality + soilAmount);
            points = Math.floor(3 * effectiveness.multiplier);
            
            if (characteristics.soilNeeds === 'high') {
              message = `🥄 Your premium ${plantData.plantType} loves this quality soil! +${soilAmount}%`;
            } else if (characteristics.soilNeeds === 'low') {
              message = `🥄 Your ${plantData.plantType} is happy with basic care! +${soilAmount}%`;
            } else {
              message = `🥄 Soil improved for your ${plantData.plantType}! +${soilAmount}%`;
            }
          } else {
            message = '🥄 Soil is already in great condition.';
          }
          break;
          
        case 'light':
          const lightAmount = Math.floor(effectiveness.baseAmount * effectiveness.multiplier);
          
          if (plantData.lightLevel < 95) {
            updates.lightLevel = Math.min(100, plantData.lightLevel + lightAmount);
            points = Math.floor(4 * effectiveness.multiplier);
            
            if (characteristics.lightNeeds === 'high') {
              message = `💡 Your ${plantData.plantType} basks in the bright light! +${lightAmount}%`;
              if (plantData.plantType.includes('Purple')) {
                message += ' Watch those colors develop!';
              }
            } else if (characteristics.lightNeeds === 'low') {
              message = `💡 Your ${plantData.plantType} appreciates the gentle lighting! +${lightAmount}%`;
            } else {
              message = `💡 Lighting optimized for ${plantData.plantType}! +${lightAmount}%`;
            }
          } else {
            message = '💡 Lighting is already optimal.';
          }
          break;
          
        case 'fertilize':
          const healthBonus = Math.floor(effectiveness.healthBonus * effectiveness.multiplier);
          const soilBonus = Math.floor(effectiveness.soilBonus * effectiveness.multiplier);
          
          if (plantData.health < 95) {
            updates.health = Math.min(100, plantData.health + healthBonus);
            updates.soilQuality = Math.min(100, plantData.soilQuality + soilBonus);
            points = Math.floor(8 * effectiveness.multiplier);
            
            if (characteristics.resilience < 0.7) {
              message = `🧪 Your sensitive ${plantData.plantType} responds amazingly to nutrients! +${healthBonus}% health`;
            } else {
              message = `🧪 Your ${plantData.plantType} appreciates the nutrition boost! +${healthBonus}% health`;
            }
          } else {
            message = '🧪 Plant is already perfectly healthy.';
          }
          break;
      }

      // 🔧 NEW: Call backend API if there are updates
      if (Object.keys(updates).length > 0) {
        const updateResponse = await GrowRoomAPI.updatePlant(plantId, {
          ...updates,
          pointsEarned: points
        });
        
        if (!updateResponse.success) {
          addNotification(`❌ ${updateResponse.error}`);
          return;
        }

        // Update local state with backend response
        setGameState(prev => ({
          ...prev,
          points: updateResponse.newPoints,
          plants: prev.plants.map((plant, index) => 
            index === plantId ? { ...plant, ...updates } : plant
          )
        }));

        updatePlantVisual(plantId, updates.health || plantData.health);
      }

      addNotification(message);

    } catch (error) {
      console.error(`Error performing ${tool} action:`, error);
      addNotification(`❌ Failed to ${tool} plant`);
    }
  }
};

  const updatePlantVisual = (plantId, health) => {
    const plant = plantsRef.current[plantId];
    if (!plant) return;

    // Get the original plant data to preserve strain characteristics
    const plantData = gameState.plants[plantId];
    if (!plantData || plantData.isRemoved) return;

    const characteristics = getPlantCharacteristics(plantData.plantType);
    
    // Update scale based on health (this part is good)
    const scale = (health / 100) * 0.8 + 0.2;
    plant.scale.set(scale, scale, scale);

    // Instead of overriding colors, apply health-based modifications to original colors
    plant.traverse((child) => {
      if (child.material && child.material.color) {
        // Skip if this is a special effect (frost, crystals, etc.)
        if (child.material.opacity < 0.9 && child.material.transparent) {
          return; // Don't modify special effects
        }

        // Get the original color based on what type of plant part this is
        let originalColor;
        
        // Determine if this is a leaf, stem, or bud based on position and parent
        const isLeaf = child.parent && (
          child.geometry && 
          child.geometry.attributes && 
          child.geometry.attributes.position && 
          child.geometry.attributes.position.count > 20 // Leaves have more vertices
        );
        
        const isStem = child.parent && 
          child.geometry instanceof THREE.CylinderGeometry;
        
        if (isLeaf) {
          // For leaves, use the strain's leaf hue
          originalColor = new THREE.Color().setHSL(
            characteristics.leafHue + (Math.random() - 0.5) * 0.05,
            0.7 + Math.random() * 0.2,
            0.3 + Math.random() * 0.3
          );
          
          // Apply special leaf effects
          if (characteristics.specialFeatures.includes('purple_leaves')) {
            originalColor.lerp(new THREE.Color(0x663366), 0.4);
          } else if (characteristics.specialFeatures.includes('blue_tint')) {
            originalColor.lerp(new THREE.Color(0x336666), 0.2);
          } else if (characteristics.specialFeatures.includes('bright_green')) {
            originalColor.multiplyScalar(1.2);
          } else if (characteristics.specialFeatures.includes('yellow_tint')) {
            originalColor.lerp(new THREE.Color(0x666633), 0.2);
          }
          
        } else if (isStem) {
          // For stems, use the strain's stem color
          originalColor = new THREE.Color(characteristics.stemColor);
          
        } else {
          // For buds and other parts, use bud color
          originalColor = new THREE.Color().setHSL(
            characteristics.budHue + (Math.random() - 0.5) * 0.1,
            0.6 + Math.random() * 0.3,
            0.4 + Math.random() * 0.2
          );
          
          // Apply special bud effects
          if (characteristics.specialFeatures.includes('golden_buds')) {
            originalColor = new THREE.Color().setHSL(0.08, 0.8, 0.6);
          } else if (characteristics.specialFeatures.includes('white_crystals')) {
            originalColor.lerp(new THREE.Color(0xffffff), 0.3);
          }
        }

        // Now apply health-based modifications to the original color
        let healthMultiplier = 1.0;
        
        if (health > 90) {
          // Excellent health - enhance the colors slightly
          healthMultiplier = 1.1;
          originalColor.multiplyScalar(healthMultiplier);
        } else if (health > 70) {
          // Good health - keep original colors
          healthMultiplier = 1.0;
        } else if (health > 50) {
          // Moderate health - slightly dull the colors
          healthMultiplier = 0.9;
          originalColor.multiplyScalar(healthMultiplier);
          // Add slight brown tint for sick look
          originalColor.lerp(new THREE.Color(0x8B4513), 0.1);
        } else if (health > 30) {
          // Poor health - significantly dull and brown
          healthMultiplier = 0.7;
          originalColor.multiplyScalar(healthMultiplier);
          originalColor.lerp(new THREE.Color(0x8B4513), 0.25);
        } else {
          // Critical health - very dull and brown
          healthMultiplier = 0.5;
          originalColor.multiplyScalar(healthMultiplier);
          originalColor.lerp(new THREE.Color(0x654321), 0.4);
        }

        // Apply the modified original color
        child.material.color.copy(originalColor);
      }
    });
  };

  const handleMouseClick = (event) => {
    // Don't handle clicks if we just finished dragging
    if (isDraggingPot) return;
    
    if (!cameraRef.current || !raycasterRef.current) return;

    const rect = mountRef.current.getBoundingClientRect();
    mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);
    const intersects = raycasterRef.current.intersectObjects([...plantsRef.current.filter(p => p), ...potsRef.current], true);

    if (intersects.length > 0) {
      const object = intersects[0].object;
      let plantId = null;

      if (object.userData.type === 'plant' || object.userData.type === 'pot') {
        plantId = object.userData.id;
      } else if (object.parent && (object.parent.userData.type === 'plant' || object.parent.userData.type === 'pot')) {
        plantId = object.parent.userData.id;
      } else if (object.parent.parent && (object.parent.parent.userData.type === 'plant' || object.parent.parent.userData.type === 'pot')) {
        plantId = object.parent.parent.userData.id;
      }

      if (plantId !== null && gameState.activeTool) {
        performAction(gameState.activeTool, plantId);
      }
    }
};

  const handleMouseMove = (event) => {
    if (!cameraRef.current || !raycasterRef.current) return;

    const rect = mountRef.current.getBoundingClientRect();
    mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);
    const intersects = raycasterRef.current.intersectObjects([...plantsRef.current.filter(p => p), ...potsRef.current], true);

    if (intersects.length > 0) {
      const object = intersects[0].object;
      let plantId = null;

      if (object.userData.type === 'plant' || object.userData.type === 'pot') {
        plantId = object.userData.id;
      } else if (object.parent && (object.parent.userData.type === 'plant' || object.parent.userData.type === 'pot')) {
        plantId = object.parent.userData.id;
      } else if (object.parent.parent && (object.parent.parent.userData.type === 'plant' || object.parent.parent.userData.type === 'pot')) {
        plantId = object.parent.parent.userData.id;
      }

      if (plantId !== null) {
        const plant = gameState.plants[plantId];
        if (plant) {
          setTooltip({
            x: event.clientX + 10,
            y: event.clientY + 10,
            plant: plant
          });
        }
      }
    } else {
      setTooltip(null);
    }
  };

  const tooltipBaseStyle = {
    position: 'absolute',
    background: 'rgba(0, 0, 0, 0.9)',
    color: 'white',
    padding: '16px',
    borderRadius: '12px',
    pointerEvents: 'none',
    maxWidth: '300px',
    zIndex: 50,
    fontSize: '14px'
  };
  const EnhancedTooltip = ({ tooltip }) => {
    if (!tooltip || tooltip.plant.isRemoved) {
      return tooltip ? (
        <div style={{...tooltipBaseStyle}}>
          <div style={{fontSize: '14px', lineHeight: '1.5'}}>
            <div>🌱 Ready for new seeds</div>
            <div style={{ color: '#10b981' }}>Select plant tool to grow!</div>
          </div>
        </div>
      ) : null;
    }

    const plant = tooltip.plant;
    const characteristics = getPlantCharacteristics(plant.plantType);
    const strainInfo = getStrainTooltipInfo(plant, characteristics);
    
    // Calculate how close to ideal conditions
    const waterStatus = getConditionStatus(plant.waterLevel, characteristics.idealWater, characteristics.waterTolerance);
    const lightStatus = getConditionStatus(plant.lightLevel, characteristics.idealLight, characteristics.lightTolerance);
    const soilStatus = getConditionStatus(plant.soilQuality, characteristics.idealSoil, characteristics.soilTolerance);
    
    return (
      <div style={{
        position: 'absolute',
        left: tooltip.x,
        top: tooltip.y,
        background: 'rgba(0, 0, 0, 0.95)',
        color: 'white',
        padding: '20px',
        borderRadius: '12px',
        pointerEvents: 'none',
        border: `2px solid ${getRarityColor(plantTypes[plant.plantType]?.rarity || 'Common')}`,
        maxWidth: '350px',
        zIndex: 50,
        fontSize: '14px'
      }}>
        <div style={{
          fontWeight: 'bold',
          color: getRarityColor(plantTypes[plant.plantType]?.rarity || 'Common'),
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '16px'
        }}>
          <span>{plant.plantType}</span>
          <span style={{
            fontSize: '10px',
            background: getRarityColor(plantTypes[plant.plantType]?.rarity || 'Common'),
            color: plantTypes[plant.plantType]?.rarity === 'Legendary' ? 'black' : 'white',
            padding: '2px 6px',
            borderRadius: '8px'
          }}>
            {plantTypes[plant.plantType]?.rarity || 'Common'}
          </span>
        </div>
        
        <div style={{ fontSize: '18px', marginBottom: '12px' }}>
          Stage: {plantTypes[plant.plantType]?.stages[plant.stage] || '🌱'} ({plant.stage + 1}/4)
        </div>
        
        <div style={{
          background: '#374151',
          borderRadius: '8px',
          padding: '8px',
          marginBottom: '12px'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '6px', color: '#10b981' }}>
            Strain Info
          </div>
          <div style={{ fontSize: '12px', marginBottom: '4px' }}>
            {strainInfo.traitDescription}
          </div>
          <div style={{ fontSize: '12px', marginBottom: '4px' }}>
            {strainInfo.needsDescription}
          </div>
          <div style={{ fontSize: '12px' }}>
            Growth Rate: {(strainInfo.growthRate * 100).toFixed(0)}% | 
            Resilience: {(strainInfo.resilience * 100).toFixed(0)}%
          </div>
        </div>
        
        <div style={{
          width: '100%',
          height: '8px',
          background: '#374151',
          borderRadius: '9999px',
          overflow: 'hidden',
          marginBottom: '12px'
        }}>
          <div style={{
            height: '100%',
            background: 'linear-gradient(90deg, #ef4444 0%, #eab308 50%, #10b981 100%)',
            transition: 'all 0.3s ease',
            width: `${plant.health}%`
          }} />
        </div>
        
        <div style={{ lineHeight: '1.6' }}>
          <div>Health: {Math.round(plant.health)}%</div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            Water: {Math.round(plant.waterLevel)}% {waterStatus.emoji}
            <span style={{ fontSize: '10px', color: waterStatus.color, marginLeft: '4px' }}>
              {waterStatus.text}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            Light: {Math.round(plant.lightLevel)}% {lightStatus.emoji}
            <span style={{ fontSize: '10px', color: lightStatus.color, marginLeft: '4px' }}>
              {lightStatus.text}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            Soil: {Math.round(plant.soilQuality)}% {soilStatus.emoji}
            <span style={{ fontSize: '10px', color: soilStatus.color, marginLeft: '4px' }}>
              {soilStatus.text}
            </span>
          </div>
          <div>Growth: {Math.round(plant.growth * 100)}%</div>
          
          <div style={{
            marginTop: '8px',
            fontSize: '12px',
            color: '#9ca3af'
          }}>
            Effects: {plantTypes[plant.plantType]?.effects.join(', ') || 'Unknown'}
          </div>
          
          <div style={{
            color: plant.needsAttention ? '#fbbf24' : '#10b981',
            marginTop: '8px',
            fontWeight: 'bold'
          }}>
            {plant.needsAttention ? '⚠️ Needs attention!' : '✅ Looking good'}
          </div>
        </div>
      </div>
    );
  };

  // Helper function to get condition status
  const getConditionStatus = (current, ideal, tolerance) => {
    const deviation = Math.abs(current - ideal);
    
    if (deviation <= 5) {
      return { emoji: '✨', text: 'Perfect', color: '#10b981' };
    } else if (deviation <= tolerance) {
      return { emoji: '✅', text: 'Good', color: '#22c55e' };
    } else if (deviation <= tolerance * 1.5) {
      return { emoji: '⚠️', text: 'OK', color: '#eab308' };
    } else {
      return { emoji: '❌', text: 'Poor', color: '#ef4444' };
    }
  };

  const animate = () => {
  if (!rendererRef.current || !sceneRef.current || !cameraRef.current) {
    console.warn('Animation stopped - missing refs');
    return;
  }

  const time = Date.now() * 0.0005;
  
  cameraRef.current.position.x = Math.sin(time * 0.3) * 0.5;
  cameraRef.current.position.z = 12 + Math.cos(time * 0.2) * 0.3;
  cameraRef.current.lookAt(0, 2, 0);

  plantsRef.current.forEach((plant, index) => {
    if (!plant) return;
    const plantTime = time + index * 0.5;
    plant.rotation.z = Math.sin(plantTime) * 0.05;
    plant.rotation.x = Math.cos(plantTime * 0.7) * 0.03;
  });

  try {
    rendererRef.current.render(sceneRef.current, cameraRef.current);
    animationIdRef.current = requestAnimationFrame(animate);
  } catch (error) {
    console.error('Render error:', error);
    animationIdRef.current = null;
  }
};

  const checkDailyBonus = () => {
  const today = new Date().toDateString();
  const lastVisit = gameState.lastVisit;
  
  // ⚠️ DON'T MANUALLY UPDATE STREAK HERE - Backend handles it
  if (lastVisit !== today) {
    const bonusPoints = 50 + (gameState.streak * 10); // Use backend streak
    
    // Give daily bonus seeds based on current streak
    const dailySeeds = {
      'Blue Dream': 1,
      'Green Crack': 1
    };
    
    if (gameState.streak >= 7) dailySeeds['Northern Lights'] = 1;
    if (gameState.streak >= 14) dailySeeds['Jack Herer'] = 1;
    if (gameState.streak >= 30) dailySeeds['White Widow'] = 1;
    
    // 🔧 ONLY UPDATE POINTS AND INVENTORY - NOT STREAK
    setGameState(prev => ({
      ...prev,
      points: prev.points + bonusPoints,
      lastVisit: today,
      // DON'T UPDATE STREAK HERE - backend handles it
      inventory: {
        ...prev.inventory,
        'Blue Dream': prev.inventory['Blue Dream'] + dailySeeds['Blue Dream'],
        'Green Crack': prev.inventory['Green Crack'] + dailySeeds['Green Crack'],
        'Northern Lights': prev.inventory['Northern Lights'] + (dailySeeds['Northern Lights'] || 0),
        'Jack Herer': prev.inventory['Jack Herer'] + (dailySeeds['Jack Herer'] || 0),
        'White Widow': prev.inventory['White Widow'] + (dailySeeds['White Widow'] || 0)
      }
    }));
    
    setBonusPoints(bonusPoints);
    setShowDailyBonus(true);
  }
};


  const handleKeyDown = (event) => {
    switch(event.key) {
      case '1': selectTool('water'); break;
      case '2': selectTool('rake'); break;
      case '3': selectTool('light'); break;
      case '4': selectTool('fertilize'); break;
      case '5': selectTool('harvest'); break;
      case '6': selectTool('remove'); break;
      case '7': selectTool('plant'); break;
      case '9': selectTool('shop'); break;
      case 'Escape': 
        selectTool(null);
        setShowPlantInventory(null);
        setSelectedPotId(null);
        break;
    }
  };

  // useEffect(() => {
  //   if (isInitializedRef.current || !mountRef.current || !hasGrowRoom) return;

  //   console.log('Mount ref ready, waiting for data...');
    
  //   const handleResize = () => {
  //     if (cameraRef.current && rendererRef.current) {
  //       cameraRef.current.aspect = window.innerWidth / window.innerHeight;
  //       cameraRef.current.updateProjectionMatrix();
  //       rendererRef.current.setSize(window.innerWidth, window.innerHeight);
  //     }
  //   };

  //   // FIXED: Add mouse move event to the mount element AND window
  //   const handleMouseMove = (event) => {
  //     // Call both the tooltip handler and drag handler
  //     handleMouseMove(event); // For tooltips
  //     handleMouseMoveDrag(event); // For dragging
  //   };

  //   // Add events to both window and mount element
  //   window.addEventListener('resize', handleResize);
  //   window.addEventListener('keydown', handleKeyDown);
  //   window.addEventListener('mouseup', handleMouseUp);
  //   window.addEventListener('mousemove', handleMouseMoveDrag); // Window level for camera
    
  //   // Add to mount element for pot dragging
  //   if (mountRef.current) {
  //     mountRef.current.addEventListener('mousemove', handleMouseMoveDrag);
  //   }

  //   return () => {
  //     window.removeEventListener('resize', handleResize);
  //     window.removeEventListener('keydown', handleKeyDown);
  //     window.removeEventListener('mouseup', handleMouseUp);
  //     window.removeEventListener('mousemove', handleMouseMoveDrag);
      
  //     // Clean up mount element events
  //     if (mountRef.current) {
  //       mountRef.current.removeEventListener('mousemove', handleMouseMoveDrag);
  //     }
      
  //     if (animationIdRef.current) {
  //       cancelAnimationFrame(animationIdRef.current);
  //     }
  //     if (rendererRef.current && mountRef.current && rendererRef.current.domElement) {
  //       if (mountRef.current.contains(rendererRef.current.domElement)) {
  //         mountRef.current.removeChild(rendererRef.current.domElement);
  //       }
  //       rendererRef.current.dispose();
  //       rendererRef.current = null;
  //     }
  //     isInitializedRef.current = false;
  //   };
  // }, [hasGrowRoom]);

  useEffect(() => {
    if (!isAuthenticated || !user || hasGrowRoom !== null) return;
    
    const initialize = async () => {
      try {
        setLoading(true);
        
        // Check grow room status
        const response = await GrowRoomAPI.getGrowRoomStatus();
        
        if (response.success && response.has_grow_room) {
          setHasGrowRoom(true);
          
          // Load data FIRST
          const dataResponse = await GrowRoomAPI.getGrowRoomData();
          if (dataResponse.success) {
            setGameState(prev => ({ ...prev, ...dataResponse.gameState }));
            
            // Initialize scene AFTER data is loaded
            setTimeout(() => {
              if (mountRef.current && !isInitializedRef.current) {
                if (initScene()) {
                  createGarden(dataResponse.gameState);
                  if (!animationIdRef.current) animate();
                }
              }
            }, 100);
          }
        } else {
          setHasGrowRoom(false);
          setShowCreateGrowRoom(true);
        }
      } catch (error) {
        console.error('Init error:', error);
        addNotification('❌ Failed to load');
      } finally {
        setLoading(false);
      }
    };
    
    initialize();
  }, [isAuthenticated, user, hasGrowRoom]);

  useEffect(() => {
    console.log('______________________')
    console.log('______________________', hasGrowRoom)
    if (!hasGrowRoom || !isInitializedRef.current) return;
    console.log('ccccc', hasGrowRoom)
    // Update plants every 30 seconds while user is actively playing
    const plantUpdateTimer = setInterval(async () => {
      try {
        console.log('🌱 Updating plant states...');
        
        // Call backend to recalculate all plant states
        const response = await GrowRoomAPI.updateAllPlants();
        
        if (response.success) {
          // Update game state with new plant data
          setGameState(prev => ({
            ...prev,
            plants: response.plants,
            gardenHealth: response.gardenHealth
          }));
          
          // Update visual plants with new health values
          response.plants.forEach((plantData, index) => {
            if (!plantData.isRemoved && plantsRef.current[index]) {
              updatePlantVisual(index, plantData.health);
            }
          });
          
          // Show notifications for plants that need attention
          const plantsNeedingAttention = response.plants.filter(p => 
            !p.isRemoved && p.needsAttention
          );
          
          if (plantsNeedingAttention.length > 0) {
            addNotification(`⚠️ ${plantsNeedingAttention.length} plants need your attention!`);
          }
        }
      } catch (error) {
        console.error('Error updating plants:', error);
      }
    }, 30000); // Every 30 seconds
    
    return () => clearInterval(plantUpdateTimer);
  }, [hasGrowRoom, isInitializedRef.current]);




  const loadGrowRoomData = async () => {
    try {
      const response = await GrowRoomAPI.getGrowRoomData();
      
      if (response.success) {
        console.log('Backend response with pot positions:', response);
        
        // 🔧 UPDATE GAME STATE TO INCLUDE POT POSITIONS
        setGameState(prev => ({
          ...prev,
          points: response.gameState.points || 0,
          streak: response.gameState.streak || 0,
          gardenHealth: response.gameState.gardenHealth || 0,
          totalHarvests: response.gameState.totalHarvests || 0,
          lastVisit: response.gameState.lastVisit || new Date().toDateString(),
          plants: response.gameState.plants || [],
          pots: response.gameState.pots || [], // 🔧 NEW: Store pot positions
          inventory: response.gameState.inventory || prev.inventory
        }));

        // Show streak XP message if awarded
        if (response.dailyXpAwarded) {
          addNotification(response.streakMessage);
        }

        // Initialize scene if needed
        if (!isInitializedRef.current && mountRef.current) {
          console.log('Initializing scene...');
          const sceneResult = initScene();
          
          if (sceneResult) {
            console.log('Scene ready, creating garden...');
            createGarden(response.gameState); // Pass full gameState including pots
            
            if (!animationIdRef.current) {
              animate();
            }
          }
        }

        await checkOfflineDegradation();
        checkDailyBonus();
      }
    } catch (error) {
      console.error('Error loading grow room data:', error);
      addNotification('❌ Failed to load garden data');
    }
  };


    const checkOfflineDegradation = async () => {
    try {
      const response = await GrowRoomAPI.checkOfflineDegradation();
      
      if (response.success && response.offline_time) {
        addNotification(response.message);
        
        // Update game state with degradation results
        if (response.degradation) {
          setGameState(prev => ({
            ...prev,
            gardenHealth: response.degradation.garden_health,
            plants: prev.plants.map(plant => {
              const update = response.degradation.updates.find(u => u.plant_id === plant.id);
              if (update) {
                return {
                  ...plant,
                  health: update.health_after,
                  needsAttention: update.needs_attention,
                  isRemoved: update.died
                };
              }
              return plant;
            })
          }));
        }
      }
    } catch (error) {
      console.error('Error checking offline degradation:', error);
    }
  };

  const snapToGrid = (x, z) => {
    let closestPos = allGridPositions[0];
    let closestIndex = 0;
    let minDistance = Infinity;
    
    allGridPositions.forEach((pos, index) => {
      const distance = Math.sqrt((x - pos[0]) ** 2 + (z - pos[1]) ** 2);
      if (distance < minDistance) {
        minDistance = distance;
        closestPos = pos;
        closestIndex = index;
      }
    });
    
    console.log(`Snapped to grid position ${closestIndex}: (${closestPos[0]}, ${closestPos[1]})`);
    return { x: closestPos[0], z: closestPos[1], gridIndex: closestIndex };
  };

  const isPositionOccupied = (targetGridIndex, excludePotIndex = null) => {
    const targetPos = allGridPositions[targetGridIndex];
    
    if (!targetPos) {
      console.log(`Invalid grid index: ${targetGridIndex}`);
      return true;
    }
    
    // 🔧 PREFER BACKEND DATA IF AVAILABLE
    if (gameState.pots && gameState.pots.length > 0) {
      const occupiedByBackend = gameState.pots.some((potData, index) => {
        // Skip if this is the pot we're dragging or if pot data is null
        if (index === excludePotIndex || !potData) return false;
        
        return Math.abs(potData.position_x - targetPos[0]) < 0.1 && 
              Math.abs(potData.position_z - targetPos[1]) < 0.1;
      });
      
      if (occupiedByBackend) {
        console.log(`Grid position ${targetGridIndex} occupied according to backend data`);
        return true;
      }
      
      // If we have backend data, trust it over visual positions
      console.log(`Grid position ${targetGridIndex} is FREE according to backend`);
      return false;
    }
    
    // Fallback to visual position checking if no backend data
    for (let potIndex = 0; potIndex < potsRef.current.length; potIndex++) {
      if (potIndex === excludePotIndex) continue;
      
      const pot = potsRef.current[potIndex];
      if (pot && 
          Math.abs(pot.position.x - targetPos[0]) < 0.1 && 
          Math.abs(pot.position.z - targetPos[1]) < 0.1) {
        console.log(`Grid position ${targetGridIndex} occupied by visual pot ${potIndex}`);
        return true;
      }
    }
    
    console.log(`Grid position ${targetGridIndex} is FREE (visual check)`);
    return false;
  };


  // Get position index from coordinates
  const getPositionIndex = (x, z) => {
    return allGridPositions.findIndex(pos => pos[0] === x && pos[1] === z);
  }


  // // Initialize scene
  // useEffect(() => {
  //   if (isInitializedRef.current || !mountRef.current) return;

  //   const timeoutId = setTimeout(() => {
  //     if (!isInitializedRef.current && mountRef.current) {
  //       initScene();
  //       createGarden();
  //       checkDailyBonus();

  //       const handleResize = () => {
  //         if (cameraRef.current && rendererRef.current) {
  //           cameraRef.current.aspect = window.innerWidth / window.innerHeight;
  //           cameraRef.current.updateProjectionMatrix();
  //           rendererRef.current.setSize(window.innerWidth, window.innerHeight);
  //         }
  //       };

  //       window.addEventListener('resize', handleResize);
  //       window.addEventListener('keydown', handleKeyDown);
  //       window.addEventListener('mouseup', handleMouseUp);
  //       window.addEventListener('mousemove', handleMouseMoveDrag);

  //       animate();
  //     }
  //   }, 10);

  //   return () => {
  //     clearTimeout(timeoutId);
  //     window.removeEventListener('resize', () => {});
  //     window.removeEventListener('keydown', handleKeyDown);
  //     window.removeEventListener('mouseup', handleMouseUp);
  //     window.removeEventListener('mousemove', handleMouseMoveDrag);
  //     if (animationIdRef.current) {
  //       cancelAnimationFrame(animationIdRef.current);
  //     }
  //     if (rendererRef.current && mountRef.current && rendererRef.current.domElement) {
  //       if (mountRef.current.contains(rendererRef.current.domElement)) {
  //         mountRef.current.removeChild(rendererRef.current.domElement);
  //       }
  //       rendererRef.current.dispose();
  //       rendererRef.current = null;
  //     }
  //     isInitializedRef.current = false;
  //   };
  // }, []);

    if (!isAuthenticated) {
      return (
        <AuthGate 
          pageName="Discover" 
          pageIcon={<Heart size={64} />}
          customMessage="Discover your perfect cannabis products with our smart recommendation engine"
          features={[
            { icon: <Heart size={20} />, text: "Swipe and rate thousands of products", color: "#ef4444" },
            { icon: <Star size={20} />, text: "Get personalized recommendations", color: "#fbbf24" },
            { icon: <TrendingUp size={20} />, text: "Build your taste profile", color: "#00d4aa" },
            { icon: <Award size={20} />, text: "Unlock achievements and rewards", color: "#8b5cf6" }
          ]}
        />
      );
    }

    if (loading || hasGrowRoom === null) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        background: 'linear-gradient(135deg, #1a4c3e 0%, #2d5a27 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '24px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>🌱</div>
          <div>Loading your grow room...</div>
        </div>
      </div>
    );
  }

  // Show create grow room modal
  if (!hasGrowRoom && showCreateGrowRoom) {
    const userLevel = userStats?.level || 1;
    const unlockedCount = userLevel >= 30 ? 19 : 
                          userLevel >= 20 ? 15 : 
                          userLevel >= 10 ? 10 : 5;
    
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        background: 'linear-gradient(135deg, #1a4c3e 0%, #2d5a27 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
          color: 'white',
          padding: '40px',
          borderRadius: '20px',
          textAlign: 'center',
          maxWidth: '500px',
          border: '2px solid #10b981',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.7)'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>🌿</div>
          <h2 style={{ 
            fontSize: '28px', 
            marginBottom: '16px',
            color: '#10b981'
          }}>
            Welcome to Cannabis Grow Room!
          </h2>
          <p style={{ 
            fontSize: '16px', 
            marginBottom: '20px',
            color: '#9ca3af',
            lineHeight: '1.5'
          }}>
            Create your personal grow room and start cultivating premium cannabis strains. 
            Based on your level ({userLevel}), you'll start with {unlockedCount} unlocked pots.
          </p>
          
          <div style={{
            background: '#374151',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            textAlign: 'left'
          }}>
            <h3 style={{ fontSize: '18px', color: '#10b981', marginBottom: '12px' }}>
              🎁 Starting Package:
            </h3>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#d1d5db' }}>
              <li>420 points</li>
              <li>3x Blue Dream seeds</li>
              <li>2x Northern Lights seeds</li>
              <li>2x Green Crack seeds</li>
              <li>1x Jack Herer seed</li>
              <li>1x White Widow seed</li>
            </ul>
          </div>
          
          <button
            onClick={createGrowRoom}
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              padding: '16px 32px',
              borderRadius: '12px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 'bold',
              transition: 'all 0.3s ease',
              boxShadow: '0 8px 20px rgba(16, 185, 129, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 12px 25px rgba(16, 185, 129, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.3)';
            }}
          >
            🌱 Create My Grow Room
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'relative',
      width: '100vw',
      height: '100vh',
      fontFamily: 'sans-serif',
      color: 'white',
      overflow: 'hidden',
      background: 'linear-gradient(135deg, #1a4c3e 0%, #2d5a27 100%)'
    }}>
      {/* Game Container */}
      <div 
        ref={mountRef} 
        style={{
          width: '100%',
          height: '100%'
        }}
        onClick={handleMouseClick}
        onMouseMove={(event) => {
          // Call both handlers - this is crucial!
          handleMouseMove(event);      // For tooltips
          handleMouseMoveDrag(event);  // For pot dragging AND camera
        }}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}  // Add this to catch mouse up on the div too
      />


      {isDraggingPot && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '12px',
          fontSize: '18px',
          fontWeight: 'bold',
          pointerEvents: 'none',
          zIndex: 1000
        }}>
          🏺 Dragging Pot #{draggedPotIndex + 1} - Drop to place
        </div>
      )}
      
      {/* UI Overlay with Inventory Count */}
      <div style={{
        position: 'absolute',
        top: '20px',
        left: '20px',
        background: 'rgba(0, 0, 0, 0.7)',
        padding: '20px',
        borderRadius: '16px',
        backdropFilter: 'blur(8px)',
        border: '2px solid #10b981'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <div style={{ width: '24px', height: '24px', background: '#fbbf24', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'black', fontSize: '14px' }}>💰</div>
          <span style={{ fontSize: '18px' }}>Points: {gameState.points}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <div style={{ width: '24px', height: '24px', background: '#f97316', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '14px' }}>🔥</div>
          <span style={{ fontSize: '18px' }}>Streak: {gameState.streak} days</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <div style={{ width: '24px', height: '24px', background: '#10b981', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '14px' }}>🌿</div>
          <span style={{ fontSize: '18px' }}>Garden Health: {gameState.gardenHealth}%</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' ,marginBottom: '12px' }}>
          <div style={{ width: '24px', height: '24px', background: '#8b5cf6', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '14px' }}>🌰</div>
          <span style={{ fontSize: '18px' }}>Seeds: {Object.values(gameState.inventory).reduce((a, b) => a + b, 0)}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '24px', height: '24px', background: '#06b6d4', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '14px' }}>✂️</div>
          <span style={{ fontSize: '18px' }}>Harvests: {gameState.totalHarvests}</span>
        </div>
      </div>
      
      {/* Enhanced Toolbox */}
      <div style={{
        position: 'absolute',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: '12px',
        background: 'rgba(0, 0, 0, 0.8)',
        padding: '16px',
        borderRadius: '24px',
        border: '2px solid #10b981'
      }}>
        {[
          { tool: 'water', icon: '💧', title: 'Water Plants (1)' },
          { tool: 'rake', icon: '🥄', title: 'Rake Soil (2)' },
          { tool: 'light', icon: '💡', title: 'Adjust Lighting (3)' },
          { tool: 'fertilize', icon: '🧪', title: 'Fertilize (4)' },
          { tool: 'harvest', icon: '✂️', title: 'Harvest (5)' },
          { tool: 'remove', icon: '🗑️', title: 'Remove Plant (6)' },
          { tool: 'plant', icon: '🌱', title: 'Plant Seed (7)' },
          { tool: 'inventory', icon: '🎒', title: 'Inventory (8)' },
          { tool: 'shop', icon: '🏪', title: 'Shop (9)' },
        ].map(({ tool, icon, title }) => (
          <button
            key={tool}
            onClick={() => selectTool(tool)}
            style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              border: '2px solid #10b981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              background: gameState.activeTool === tool 
                ? (tool === 'remove' ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' : 
                   tool === 'plant' ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)' :
                   tool === 'shop' ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' :
                   'linear-gradient(135deg, #10b981 0%, #059669 100%)') 
                : 'linear-gradient(135deg, #166534 0%, #14532d 100%)',
              boxShadow: gameState.activeTool === tool 
                ? (tool === 'remove' ? '0 8px 20px rgba(220, 38, 38, 0.5)' :
                   tool === 'plant' ? '0 8px 20px rgba(22, 163, 74, 0.5)' :
                   '0 8px 20px rgba(16, 185, 129, 0.5)') 
                : 'none'
            }}
            title={title}
          >
            {icon}
          </button>
        ))}
      </div>


            {showShop && (
        <React.Suspense fallback={
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            color: 'white',
            fontSize: '20px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏪</div>
              <div>Loading Shop...</div>
            </div>
          </div>
        }>
          <CannabisShop
            userPoints={gameState.points}
            userInventory={gameState.inventory}
            onPurchase={handleShopPurchase}
            onClose={handleCloseShop}
            catalog={shopCatalog}
            loading={shopLoading}
          />
        </React.Suspense>
      )}

      {/* Enhanced Notifications with Shop Messages */}
      <div style={{
    position: 'fixed',
    top: '20px',
    right: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px', // Tighter spacing
    maxWidth: '350px',
    zIndex: 1000,
    pointerEvents: 'none',
    maxHeight: '80vh', // Prevent overflow
    overflowY: 'auto'
  }}>
    {notifications.slice(-5).map((notification, index) => { // Only show last 5
      let notificationStyle = {
        background: 'rgba(16, 185, 129, 0.95)',
        borderLeft: '4px solid #047857',
        color: 'white'
      };
      
      // Your existing style logic...
      if (notification.message.includes('❌')) {
        notificationStyle = {
          background: 'rgba(239, 68, 68, 0.95)',
          borderLeft: '4px solid #dc2626',
          color: 'white'
        };
      } else if (notification.message.includes('💧')) {
        notificationStyle = {
          background: 'rgba(59, 130, 246, 0.95)',
          borderLeft: '4px solid #3b82f6',
          color: 'white'
        };
      } else if (notification.message.includes('💡')) {
        notificationStyle = {
          background: 'rgba(251, 191, 36, 0.95)',
          borderLeft: '4px solid #f59e0b',
          color: 'black'
        };
      }
      
      return (
        <div
          key={notification.id}
          style={{
            ...notificationStyle,
            padding: '8px 12px', // Smaller padding for actions
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: '500',
            lineHeight: '1.3',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            animation: `slideInRight 0.2s ease-out both`,
            maxWidth: '100%',
            pointerEvents: 'auto',
            cursor: 'pointer',
            transition: 'opacity 0.2s ease',
            opacity: Math.max(0.7, 1 - (index * 0.1)) // Fade older notifications
          }}
          onClick={() => {
            setNotifications(prev => prev.filter(n => n.id !== notification.id));
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '6px'
          }}>
            <span style={{ flex: 1, fontSize: '12px' }}>
              {notification.message}
            </span>
            <span style={{ 
              fontSize: '14px', 
              opacity: 0.7,
              marginLeft: '4px'
            }}>×</span>
          </div>
        </div>
      );
    })}
  </div>
      <style>{`
        @keyframes slideInRight {
          from { 
            transform: translateX(100%) translateY(var(--index-offset, 0px)); 
            opacity: 0; 
          }
          to { 
            transform: translateX(0) translateY(var(--index-offset, 0px)); 
            opacity: 1; 
          }
        }
        
        @keyframes slideIn {
          from { 
            transform: translateX(100%); 
            opacity: 0; 
          }
          to { 
            transform: translateX(0); 
            opacity: 1; 
          }
        }
        
        @keyframes bounce {
          0%, 20%, 53%, 80%, 100% {
            animation-timing-function: cubic-bezier(0.215, 0.610, 0.355, 1.000);
            transform: translate3d(0,0,0);
          }
          40%, 43% {
            animation-timing-function: cubic-bezier(0.755, 0.050, 0.855, 0.060);
            transform: translate3d(0, -30px, 0);
          }
          70% {
            animation-timing-function: cubic-bezier(0.755, 0.050, 0.855, 0.060);
            transform: translate3d(0, -15px, 0);
          }
        90% {
            transform: translate3d(0,-4px,0);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
        }
      `}</style>


      {/* Shop Achievement Notifications */}
      {purchaseHistory.length > 0 && (
        <div style={{
          position: 'absolute',
          bottom: '100px',
          right: '20px',
          background: 'rgba(139, 92, 246, 0.95)',
          color: 'white',
          padding: '12px 16px',
          borderRadius: '12px',
          fontSize: '14px',
          border: '2px solid #8b5cf6',
          boxShadow: '0 0 20px rgba(139, 92, 246, 0.5)',
          animation: 'pulse 2s infinite'
        }}>
          🏆 Recent Purchase: {purchaseHistory[0]?.name || purchaseHistory[0]?.item}
        </div>
      )}

      {/* Shop Quick Stats in UI Overlay */}
      <div style={{
        position: 'absolute',
        top: '20px',
        left: '20px',
        background: 'rgba(0, 0, 0, 0.7)',
        padding: '20px',
        borderRadius: '16px',
        backdropFilter: 'blur(8px)',
        border: '2px solid #10b981'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <div style={{ width: '24px', height: '24px', background: '#fbbf24', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'black', fontSize: '14px' }}>💰</div>
          <span style={{ fontSize: '18px' }}>Points: {gameState.points.toLocaleString()}</span>
          {/* Add shop indicator when points are high */}
          {gameState.points >= 100 && (
            <span style={{ 
              fontSize: '12px', 
              background: '#8b5cf6', 
              color: 'white', 
              padding: '2px 6px', 
              borderRadius: '8px',
              animation: 'pulse 2s infinite'
            }}>
              Shop Available!
            </span>
          )}
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <div style={{ width: '24px', height: '24px', background: '#f97316', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '14px' }}>🔥</div>
          <span style={{ fontSize: '18px' }}>Streak: {gameState.streak} days</span>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <div style={{ width: '24px', height: '24px', background: '#10b981', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '14px' }}>🌿</div>
          <span style={{ fontSize: '18px' }}>Garden Health: {gameState.gardenHealth}%</span>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <div style={{ width: '24px', height: '24px', background: '#8b5cf6', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '14px' }}>🌰</div>
          <span style={{ fontSize: '18px' }}>
            Seeds: {Object.values(gameState.inventory).reduce((a, b) => a + b, 0)}
            {/* Show seed variety count */}
            <span style={{ fontSize: '12px', color: '#9ca3af', marginLeft: '4px' }}>
              ({Object.entries(gameState.inventory).filter(([_, qty]) => qty > 0).length} types)
            </span>
          </span>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '24px', height: '24px', background: '#06b6d4', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '14px' }}>✂️</div>
          <span style={{ fontSize: '18px' }}>Harvests: {gameState.totalHarvests}</span>
        </div>
      </div>

      {showInventory && (
  <div style={{
    position: 'absolute',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 200
  }}>
    <div style={{
      background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
      color: 'white',
      padding: '24px',
      borderRadius: '16px',
      maxWidth: '90vw',
      width: 'auto',
      border: '2px solid #10b981',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.7)'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: '#10b981',
          margin: 0
        }}>🎒 Inventory</h2>
        
        <button
          onClick={() => setShowInventory(false)}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#9ca3af',
            fontSize: '24px',
            cursor: 'pointer',
            padding: '4px'
          }}
        >
          ✕
        </button>
      </div>
      
      {/* Inventory Items - Horizontal Layout */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        justifyContent: 'center',
        maxWidth: '800px'
      }}>
        {Object.entries(gameState.inventory).map(([itemName, quantity]) => {
          if (quantity === 0) return null;
          
          const plant = plantTypes[itemName];
          const rarityColor = plant ? getRarityColor(plant.rarity) : '#6b7280';
          
          return (
            <div
              key={itemName}
              style={{
                background: 'linear-gradient(135deg, #374151 0%, #1f2937 100%)',
                border: `2px solid ${rarityColor}`,
                borderRadius: '12px',
                padding: '12px',
                minWidth: '100px',
                textAlign: 'center',
                boxShadow: `0 0 8px ${rarityColor}30`,
                position: 'relative'
              }}
            >
              {/* Item Icon */}
              <div style={{
                fontSize: '32px',
                marginBottom: '8px'
              }}>
                {plant ? plant.stages[0] : '🌱'}
              </div>
              
              {/* Item Name */}
              <div style={{
                fontSize: '12px',
                fontWeight: 'bold',
                color: rarityColor,
                marginBottom: '4px',
                textTransform: 'capitalize'
              }}>
                {itemName}
              </div>
              
              {/* Quantity Badge */}
              <div style={{
                position: 'absolute',
                top: '-6px',
                right: '-6px',
                background: '#10b981',
                color: 'white',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold',
                border: '2px solid #111827'
              }}>
                {quantity}
              </div>
              
              {/* Rarity (if plant) */}
              {plant && (
                <div style={{
                  fontSize: '10px',
                  color: rarityColor,
                  fontWeight: 'bold',
                  marginTop: '4px'
                }}>
                  {plant.rarity}
                </div>
              )}
            </div>
          );
        })}
        
        {/* Empty State */}
        {Object.values(gameState.inventory).every(qty => qty === 0) && (
          <div style={{
            textAlign: 'center',
            color: '#9ca3af',
            padding: '40px',
            fontSize: '16px'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📦</div>
            <div>Your inventory is empty</div>
            <div style={{ fontSize: '14px', marginTop: '8px' }}>
              Visit the shop to buy seeds!
            </div>
          </div>
        )}
      </div>
      
      {/* Footer Stats */}
      <div style={{
        marginTop: '20px',
        paddingTop: '16px',
        borderTop: '1px solid #374151',
        display: 'flex',
        justifyContent: 'center',
        gap: '24px',
        fontSize: '14px'
      }}>
        <div style={{ color: '#9ca3af' }}>
          <span style={{ color: '#10b981', fontWeight: 'bold' }}>
            {Object.values(gameState.inventory).reduce((total, qty) => total + qty, 0)}
          </span> total items
        </div>
        
        <div style={{ color: '#9ca3af' }}>
          <span style={{ color: '#10b981', fontWeight: 'bold' }}>
            {Object.entries(gameState.inventory).filter(([_, qty]) => qty > 0).length}
          </span> unique types
        </div>
      </div>
    </div>
  </div>
)}
      
      {/* Plant Inventory Selection Modal */}
      {showPlantInventory && (
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 200
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
            color: 'white',
            padding: '32px',
            borderRadius: '20px',
            maxWidth: '90vw',
            maxHeight: '80vh',
            overflow: 'auto',
            border: '2px solid #10b981',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.7)'
          }}>
            <div style={{
              textAlign: 'center',
              marginBottom: '24px'
            }}>
              <h2 style={{
                fontSize: '28px',
                fontWeight: 'bold',
                color: '#10b981',
                marginBottom: '8px'
              }}>🌱 Choose Your Seed</h2>
              <p style={{
                color: '#9ca3af',
                fontSize: '16px'
              }}>Select a plant variety to grow in your garden</p>
            </div>
            
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '16px',
              marginBottom: '24px',
              justifyContent: 'center'
            }}>
              {Object.entries(plantTypes).map(([plantName, plant]) => {
                const hasSeeds = gameState.inventory[plantName] > 0;
                const rarityColor = getRarityColor(plant.rarity);
                
                return (
                  <div
                    key={plantName}
                    onClick={() => hasSeeds ? plantSelectedSeed(plantName, selectedPotId) : null}
                    style={{
                      background: hasSeeds ? 'linear-gradient(135deg, #374151 0%, #1f2937 100%)' : 'linear-gradient(135deg, #4b5563 0%, #374151 100%)',
                      border: `2px solid ${rarityColor}`,
                      borderRadius: '16px',
                      padding: '20px',
                      cursor: hasSeeds ? 'pointer' : 'not-allowed',
                      transition: 'all 0.3s ease',
                      opacity: hasSeeds ? 1 : 0.5,
                      boxShadow: hasSeeds ? getRarityGlow(plant.rarity) : 'none',
                      minWidth: '300px',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                    onMouseEnter={(e) => {
                      if (hasSeeds) {
                        e.target.style.transform = 'scale(1.02)';
                        e.target.style.boxShadow = getRarityGlow(plant.rarity);
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (hasSeeds) {
                        e.target.style.transform = 'scale(1)';
                        e.target.style.boxShadow = getRarityGlow(plant.rarity);
                      }
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '12px'
                    }}>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: 'bold',
                        color: rarityColor,
                        margin: 0
                      }}>{plantName}</h3>
                      
                      <div style={{
                        background: rarityColor,
                        color: plant.rarity === 'Legendary' ? 'black' : 'white',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {plant.rarity}
                      </div>
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '12px',
                      fontSize: '24px',
                      justifyContent: 'center'
                    }}>
                      {plant.stages.map((stage, index) => (
                        <span key={index} style={{ marginRight: '4px' }}>{stage}</span>
                      ))}
                    </div>
                    
                    <div style={{
                      fontSize: '14px',
                      color: '#d1d5db',
                      marginBottom: '12px',
                      textAlign: 'center'
                    }}>
                      <strong>Effects:</strong> {plant.effects.join(', ')}
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '14px'
                    }}>
                      <span style={{
                        color: '#10b981',
                        fontWeight: 'bold'
                      }}>
                        Base Health: {plant.baseHealth}%
                      </span>
                      
                      <span style={{
                        color: hasSeeds ? '#10b981' : '#ef4444',
                        fontWeight: 'bold'
                      }}>
                        Seeds: {gameState.inventory[plantName]}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div style={{
              textAlign: 'center'
            }}>
              <button
                onClick={() => {
                  setShowPlantInventory(null);
                  setSelectedPotId(null);
                }}
                style={{
                  background: '#6b7280',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  transition: 'background 0.3s ease'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Enhanced Tooltip */}
<EnhancedTooltip tooltip={tooltip} />
      
      {/* Plant Removal Confirmation Dialog */}
      {showRemoveConfirm && (
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
            color: 'white',
            padding: '32px',
            borderRadius: '20px',
            textAlign: 'center',
            maxWidth: '400px',
            border: '2px solid #dc2626',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.7)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🗑️</div>
            
            <h3 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#ef4444',
              marginBottom: '16px'
            }}>
              Remove Plant?
            </h3>
            
            <div style={{
              fontSize: '18px',
              marginBottom: '8px',
              color: getRarityColor(plantTypes[showRemoveConfirm.strain]?.rarity || 'Common')
            }}>
              {showRemoveConfirm.strain}
            </div>
            
            <div style={{
              fontSize: '14px',
              color: '#9ca3af',
              marginBottom: '20px',
              lineHeight: '1.5'
            }}>
              Health: {Math.round(showRemoveConfirm.health)}%<br/>
              You'll earn {showRemoveConfirm.points} points for composting<br/>
              <span style={{ color: '#10b981' }}>
                Chance to recover seeds from this plant!
              </span>
            </div>
            
            <div style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => setShowRemoveConfirm(null)}
                style={{
                  background: '#6b7280',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  transition: 'background 0.3s ease'
                }}
              >
                Cancel
              </button>
              
              <button
                onClick={() => removePlant(showRemoveConfirm.plantId)}
                style={{
                  background: '#dc2626',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  transition: 'background 0.3s ease'
                }}
              >
                Remove Plant
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Enhanced Daily Bonus Modal */}
      {showDailyBonus && (
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
            color: 'black',
            padding: '32px',
            borderRadius: '24px',
            textAlign: 'center',
            fontWeight: 'bold',
            fontSize: '24px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
            animation: 'bounce 1s infinite',
            maxWidth: '400px'
          }}>
            <div style={{ marginBottom: '16px' }}>🎉 Welcome Back! 🎉</div>
            <div style={{ marginBottom: '8px' }}>Daily Bonus: +{bonusPoints} Points</div>
            <div style={{ marginBottom: '16px' }}>Streak: {gameState.streak} days</div>
            
            <div style={{
              fontSize: '16px',
              marginBottom: '20px',
              color: '#059669'
            }}>
              🌰 Free seeds included!
            </div>
            
            <button
              onClick={() => setShowDailyBonus(false)}
              style={{
                background: '#10b981',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '12px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold',
                transition: 'background 0.3s ease'
              }}
            >
              Awesome!
            </button>
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes slideIn {
          from { 
            transform: translateX(100%); 
            opacity: 0; 
          }
          to { 
            transform: translateX(0); 
            opacity: 1; 
          }
        }
        @keyframes bounce {
          0%, 20%, 53%, 80%, 100% {
            animation-timing-function: cubic-bezier(0.215, 0.610, 0.355, 1.000);
            transform: translate3d(0,0,0);
          }
          40%, 43% {
            animation-timing-function: cubic-bezier(0.755, 0.050, 0.855, 0.060);
            transform: translate3d(0, -30px, 0);
          }
          70% {
            animation-timing-function: cubic-bezier(0.755, 0.050, 0.855, 0.060);
            transform: translate3d(0, -15px, 0);
          }
         90% {
            transform: translate3d(0,-4px,0);
          }
        }
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
        }
      `}</style>
    </div>
  );
};


export default CannabisGrowRoom;